import GoogleGeoProposal from "./GoogleGeoProposal";
// src/components/CreateLoad.js 
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import "../styles/CreateLoad.css";
import { FaCalendarAlt, FaCheckCircle } from "react-icons/fa";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import PremiumAiImportProgressExperience from "./create-load/AiImportProgressExperience";
import CreateLoadTimeSchedulePicker from "./create-load/CreateLoadTimeSchedulePicker";
import {
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiInfo,
  FiExternalLink,
  FiCheck,
  FiLink,
  FiFileText,
  FiMapPin,
  FiTruck,
  FiMove,
  FiFlag,
  FiChevronLeft,
  FiChevronRight,
  FiBox,
  FiMail,

} from "react-icons/fi";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { useTranslation } from "react-i18next";
import LoadDocumentUploader from "./create-load/LoadDocumentUploader";
import CreateLoadAtlasPanel from "./create-load/CreateLoadAtlasPanel";
import "../styles/CreateLoadAtlas.css";
import "../styles/CreateLoadPremiumV6.css";
import CreateLoadStopCard from "./create-load/CreateLoadStopCard";
import aiImportHeroRobot from "../assets/ai-journey/robot-upload.JPG";


import {
  importTransportOrderAsync,
  getLoadIntelligenceProgressLabel,
} from "../lib/loadIntelligenceAsync";

const MAX_POINTS = 7;

// Same physical sorting behavior as the Transport Session, vertical for Create Load.
const CreateLoadSortableStop = ({
  id,
  kind,
  index,
  disabled,
  dragInProgress,
  onArrowMove,
  children,
}) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
    animateLayoutChanges: () => false,
    transition: {
      duration: 180,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    },
  });

  const activeTransform =
    dragInProgress && transform
      ? CSS.Translate.toString({ ...transform, x: 0 })
      : undefined;

  const handleKeyDown = (event) => {
    if (
      !isDragging &&
      (event.key === "ArrowUp" || event.key === "ArrowDown")
    ) {
      onArrowMove(event);
      return;
    }

    listeners?.onKeyDown?.(event);
  };

  return (
    <div
      ref={setNodeRef}
      id={`create-load-atlas-${kind}-${index}`}
      data-stop-kind={kind}
      data-stop-id={id}
      className={`create-load-multi-location-input loadevo-v6-stop-mount${
        isDragging ? " is-dragging" : ""
      }`}
      style={{
        transform: activeTransform,
        transition:
          dragInProgress && !isDragging ? transition : "none",
        zIndex: isDragging ? 100 : undefined,
      }}
    >
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="loadevo-v6-stop-drag-handle"
        tabIndex={disabled ? -1 : 0}
        title="Drag to reorder · Arrow Up/Down for keyboard"
        aria-label={`Reorder ${kind} ${index + 1}. Use arrow keys to move.`}
        onKeyDown={handleKeyDown}
      >
        <FiMove aria-hidden="true" />
      </span>

      {children}
    </div>
  );
};

// Change only the displayed location name.
// Preserve address, coordinates, Google Maps and other stop details.
const renameStopLocationPreservingDetails = (place, selectedOption) => {
  const location = String(selectedOption?.label || "").trim();
  const unchanged = location === String(place.location || "").trim();

  return {
    ...place,
    location,
    placeId: unchanged ? place.placeId : null,
    isNewPlace: unchanged
      ? place.isNewPlace
      : Boolean(location) && !place.placeId,
  };
};

// UI-only identities: never sent to the backend.
let nextStopUiNumber = 0;
const createStopUiId = () => `create-load-stop-${++nextStopUiNumber}`;

const stopScheduleValue = (point) => {
  const date = String(point?.date || "").trim();
  const hour = String(point?.hour || "").trim();

  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    !/^([01]\d|2[0-3]):[0-5]\d$/.test(hour)
  ) {
    return null;
  }

  return `${date}T${hour}`;
};

const formatReorderSchedule = (point) => {
  if (!stopScheduleValue(point)) return "Not scheduled";

  const [year, month, day] = point.date.split("-");

  return `${day}/${month}/${year} · ${point.hour}`;
};

const findStopSequenceConflict = (points) => {
  for (let index = 1; index < points.length; index += 1) {
    const previous = stopScheduleValue(points[index - 1]);
    const current = stopScheduleValue(points[index]);

    if (previous && current && previous > current) {
      return { beforeIndex: index - 1, afterIndex: index };
    }
  }

  return null;
};

// Move index-based validation errors with their original stops.
const remapStopErrors = (errors, kind, before, after, hasField) => {
  const idToIndex = new Map(
    after.map((point, index) => [point._uiId, index])
  );

  const result = {};

  for (const [key, value] of Object.entries(errors)) {
    const match = hasField
      ? key.match(/^(pickup|delivery)-(.+)-(\d+)$/)
      : key.match(/^(pickup|delivery)-(\d+)$/);

    if (!match || match[1] !== kind) {
      result[key] = value;
      continue;
    }

    const oldIndex = Number(match[hasField ? 3 : 2]);
    const id = before[oldIndex]?._uiId;
    const newIndex = idToIndex.get(id);

    if (newIndex === undefined) continue;

    const nextKey = hasField
      ? `${kind}-${match[2]}-${newIndex}`
      : `${kind}-${newIndex}`;

    result[nextKey] = value;
  }

  return result;
};


// Only enrich fields that the dispatcher has not changed since the
// preliminary import. Never transfer geographical evidence to a
// different stop, changed address, or changed Place selection.
const sameImportedValue = (left, right) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const mergeFinalImportLocations = (current, preliminary, final) => {
  if (!Array.isArray(current) || !Array.isArray(preliminary) ||
      !Array.isArray(final) || current.length !== preliminary.length ||
      current.length !== final.length) {
    return current;
  }

  const identityFields = [
    "location", "address", "postalCode", "city", "country", "placeId",
    "coordinates", "gMapsLink", "isNewPlace", "aiGeocode",
  ];
  const safeFields = [
    "location", "placeId", "address", "houseNumber", "postalCode",
    "city", "country", "coordinates", "gMapsLink", "contactPhone",
    "workingHours", "loadingAccess", "handlingEquipment",
    "handlingEquipmentOther", "isNewPlace", "aiPlaceResolution",
    "aiGeocode", "aiLocationResolution",
  ];

  return current.map((point, index) => {
    const original = preliminary[index];
    const enriched = final[index];
    if (!point || !original || !enriched ||
        !identityFields.every(field => sameImportedValue(point[field], original[field]))) {
      return point;
    }

    const updates = {};
    safeFields.forEach(field => {
      if (sameImportedValue(point[field], original[field])) {
        updates[field] = enriched[field];
      }
    });
    return { ...point, ...updates };
  });
};


const formatAiImportFileSize = (
  bytes
) => {
  const value = Number(
    bytes
  );

  if (
    !Number.isFinite(value) ||
    value <= 0
  ) {
    return "";
  }

  if (
    value <
    1024 * 1024
  ) {
    return `${(
      value / 1024
    ).toFixed(1)} KB`;
  }

  return `${(
    value /
    (1024 * 1024)
  ).toFixed(1)} MB`;
};


const getAiImportStageIndex = (
  job
) => {
  const status = String(
    job?.status || ""
  )
    .trim()
    .toUpperCase();

  const stage = String(
    job?.stage || ""
  )
    .trim()
    .toUpperCase();

  if (
    status === "COMPLETED"
  ) {
    return 4;
  }

  switch (stage) {
    case "UPLOAD_RECEIVED":
      return 0;

    case "PROCESSING":
    case "DOCUMENT_ANALYSIS":
    case "INTELLIGENCE_PROCESSING":
    case "RECOVERED":
      return 1;

    case "LOCATION_RESOLUTION":
      return 2;

    default:
      return 1;
  }
};


const AiImportProgressExperience = ({
  job,
  stops = [],
}) => {
  if (!job) {
    return null;
  }

  const status = String(
    job?.status || ""
  )
    .trim()
    .toUpperCase();

  const completed =
    status === "COMPLETED";

  const failed =
    status === "FAILED";

  const stageIndex =
    getAiImportStageIndex(
      job
    );

  // No measurable total duration: show real stages, not fake percentages.
  // The completed state alone is objectively 100%.

  const fileName =
    String(
      job?.fileName ||
      "Transport order"
    ).trim();

  const fileSize =
    formatAiImportFileSize(
      job?.fileSize
    );

  const resolvedStops =
    stops.filter(
      (stop) =>
        String(
          stop?.point
            ?.coordinates ||
          ""
        ).trim()
    ).length;

  const pickupCount =
    stops.filter(
      (stop) =>
        stop?.type ===
        "PICKUP"
    ).length;

  const deliveryCount =
    stops.filter(
      (stop) =>
        stop?.type ===
        "DELIVERY"
    ).length;

  const stages = [
    "Document received",
    "Reading transport order",
    "Verifying locations",
    "Load ready",
  ];

  const secondaryText =
    failed
      ? (
          job?.error?.message ||
          "The document could not be analysed."
        )
      : completed
      ? (
          stops.length
            ? `${pickupCount} pickup${
                pickupCount === 1
                  ? ""
                  : "s"
              } · ${deliveryCount} deliver${
                deliveryCount === 1
                  ? "y"
                  : "ies"
              } · ${resolvedStops}/${stops.length} locations with coordinates`
            : "The extracted fields are ready."
        )
      : String(job?.stage || "").toUpperCase() === "LOCATION_RESOLUTION"
      ? "Document fields are ready to edit. Location verification continues in the background."
      : "LOADEvo is reading your document. Fields will appear as soon as extraction finishes.";

  return (
    <section
      className={[
        "load-ai-import-experience",
        completed
          ? "is-completed"
          : "",
        failed
          ? "is-failed"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="load-ai-import-experience-file">
        <div className="load-ai-import-experience-file-icon">
          <FiFileText
            aria-hidden="true"
          />
        </div>

        <div className="load-ai-import-experience-file-copy">
          <strong>
            {fileName}
          </strong>

          <span>
            {[
              fileSize,
              completed
                ? "Processed"
                : failed
                ? "Processing stopped"
                : "Processing...",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </div>

      <div className="load-ai-import-experience-heading">
        <div>
          <strong>
            {completed
              ? "Transport order processed"
              : failed
              ? "Transport order could not be processed"
              : getLoadIntelligenceProgressLabel(
                  job
                )}
          </strong>

          <span>
            {secondaryText}
          </span>
        </div>

        {!failed && (
          <span className="load-ai-import-experience-progress-value">
            {completed
              ? "Ready"
              : "Working in background"}
          </span>
        )}
      </div>

      {completed && (
        <div className="load-ai-import-experience-track"
             role="progressbar" aria-label="Transport order import complete"
             aria-valuemin="0" aria-valuemax="100" aria-valuenow={100}>
          <div className="load-ai-import-experience-bar" style={{ width: "100%" }} />
        </div>
      )}

      {!failed && (
        <div className="load-ai-import-experience-stages">
          {stages.map(
            (
              label,
              index
            ) => {
              const done =
                completed ||
                index <
                  stageIndex;

              const active =
                !completed &&
                index ===
                  stageIndex;

              return (
                <div
                  key={label}
                  className={[
                    "load-ai-import-experience-stage",
                    done
                      ? "is-done"
                      : "",
                    active
                      ? "is-active"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="load-ai-import-experience-stage-dot">
                    {done
                      ? "✓"
                      : ""}
                  </span>

                  <span>
                    {label}
                  </span>
                </div>
              );
            }
          )}
        </div>
      )}

      {completed &&
        stops.length > 0 && (
          <div className="load-ai-import-route">
            {stops.map(
              (
                stop,
                index
              ) => {
                const point =
                  stop?.point ||
                  {};

                const resolved =
                  Boolean(String(point?.coordinates || "").trim());

                const verified = resolved &&
                  point?.aiGeocode?.requiresReview !== true &&
                  point?.aiPlaceResolution?.requiresReview !== true &&
                  (
                    String(point?.aiGeocode?.status || "").toUpperCase() === "MATCH" ||
                    String(point?.aiPlaceResolution?.status || "").toUpperCase() === "MATCH"
                  );

                const location =
                  String(
                    point?.location ||
                    point?.placeName ||
                    (
                      stop?.type ===
                      "PICKUP"
                        ? "Pickup"
                        : "Delivery"
                    )
                  ).trim();

                const cityCountry =
                  [
                    point?.city,
                    point?.country,
                  ]
                    .filter(Boolean)
                    .join(", ");

                return (
                  <React.Fragment
                    key={
                      stop?.key ||
                      `${stop?.type}-${index}`
                    }
                  >
                    <div
                      className={[
                        "load-ai-import-route-stop",
                        verified
                          ? "is-verified"
                          : "is-unresolved",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <span className="load-ai-import-route-dot">
                        {verified
                          ? "✓"
                          : ""}
                      </span>

                      <strong>
                        {stop?.type ===
                        "PICKUP"
                          ? "Pickup"
                          : "Delivery"}
                      </strong>

                      <span className="load-ai-import-route-name">
                        {location}
                      </span>

                      {cityCountry && (
                        <small>
                          {cityCountry}
                        </small>
                      )}
                    </div>

                    {index <
                      stops.length -
                        1 && (
                      <div
                        className={[
                          "load-ai-import-route-line",
                          verified
                            ? "is-verified"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-hidden="true"
                      />
                    )}
                  </React.Fragment>
                );
              }
            )}
          </div>
        )}
    </section>
  );
};


const WorkflowNode = ({
  active = false,
  success = false,
  warning = false,
  children,
}) => {
  return (
    <div
      className={[
        "coordinate-workflow-node",
        active ? "is-active" : "",
        success ? "is-success" : "",
        warning ? "is-warning" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
};

const WorkflowArrow = () => (
  <div className="coordinate-workflow-arrow">
    ↓
  </div>
);

const WorkflowBranch = ({
  active,
  type,
  children,
}) => (
  <div
    className={[
      "coordinate-workflow-branch-label",
      type === "yes"
        ? "coordinate-workflow-branch-label--yes"
        : "coordinate-workflow-branch-label--no",
      active
        ? "is-active"
        : "",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    {children}
  </div>
);


const GoogleMapsLinkField = ({
  value,
  onChange,
  placeholder,
  invalid = false,
  mainField = false,
}) => {
  const rawLink = String(
    value || ""
  ).trim();

  let safeUrl = "";

  if (rawLink) {
    try {
      const candidate =
        /^https?:\/\//i.test(
          rawLink
        )
          ? rawLink
          : `https://${rawLink}`;

      const parsed = new URL(
        candidate
      );

      if (
        parsed.protocol === "https:" ||
        parsed.protocol === "http:"
      ) {
        safeUrl = parsed.href;
      }
    } catch {
      safeUrl = "";
    }
  }

  const handleOpen = () => {
    if (!safeUrl) {
      return;
    }

    window.open(
      safeUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div
      className={[
        "create-load-google-maps-field",
        invalid
          ? "is-error"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <img
        className="create-load-google-maps-logo"
        src="https://www.gstatic.com/images/branding/product/2x/maps_96dp.png"
        alt=""
        aria-hidden="true"
      />

      <input
        type="text"
        placeholder={placeholder}
        value={value || ""}
        onChange={onChange}
        className={[
          "create-load-input",
          mainField
            ? "create-load-main-field"
            : "",
          "create-load-google-maps-input",
          invalid
            ? "input-error"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
      />

      <div
        className="create-load-google-maps-actions"
      >
        <button
          type="button"
          className="create-load-google-maps-open"
          onClick={handleOpen}
          disabled={!safeUrl}
          title={
            safeUrl
              ? "Open in Google Maps"
              : "Enter a valid Google Maps link"
          }
          aria-label="Open in Google Maps"
        >
          <FiExternalLink
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
};


const flattenLocationWorkflowTrace = (
  point
) => {
  const rawTrace =
    Array.isArray(
      point
        ?.aiLocationResolution
        ?.workflowTrace
    )
      ? point
          .aiLocationResolution
          .workflowTrace
      : [];

  const output = [];

  rawTrace.forEach(
    (item) => {
      if (
        item?.stage ===
          "INTELLIGENCE_TRACE" &&
        item?.detail
      ) {
        output.push(
          item.detail
        );

        return;
      }

      output.push(
        item
      );
    }
  );

  return output;
};

const findWorkflowStage = (
  trace,
  stage
) =>
  trace.find(
    (item) =>
      item?.stage ===
      stage
  ) || null;

const CoordinateResolutionWorkflow = ({
  stops = [],
}) => {
  const [
    selectedKey,
    setSelectedKey,
  ] = useState(
    stops[0]?.key ||
    null
  );

  useEffect(
    () => {
      if (!stops.length) {
        setSelectedKey(
          null
        );

        return;
      }

      const stillExists =
        stops.some(
          (stop) =>
            stop.key ===
            selectedKey
        );

      if (!stillExists) {
        setSelectedKey(
          stops[0].key
        );
      }
    },
    [
      stops,
      selectedKey,
    ]
  );

  const selected =
    stops.find(
      (stop) =>
        stop.key ===
        selectedKey
    ) ||
    stops[0];

  if (!selected) {
    return null;
  }

  const point =
    selected.point ||
    {};

  const trace =
    flattenLocationWorkflowTrace(
      point
    );

  const input =
    findWorkflowStage(
      trace,
      "INPUT"
    );

  const loadevo =
    findWorkflowStage(
      trace,
      "LOADEVO_ENRICHMENT"
    );

  const loadevoFill =
    findWorkflowStage(
      trace,
      "LOADEVO_PLACE_FILL"
    );

  const intelligence =
    findWorkflowStage(
      trace,
      "INTELLIGENCE_ADDRESS_ENRICHMENT"
    );

  const registry =
    findWorkflowStage(
      trace,
      "COUNTRY_REGISTRY"
    );

  const registryFill =
    findWorkflowStage(
      trace,
      "COUNTRY_REGISTRY_FILL"
    );

  const web =
    findWorkflowStage(
      trace,
      "WEB_DISCOVERY"
    );

  const webFill =
    findWorkflowStage(
      trace,
      "WEB_DISCOVERY_FILL"
    );

  const geocoder =
    findWorkflowStage(
      trace,
      "PHYSICAL_ADDRESS_GEOCODER"
    );

  const finalDecision =
    findWorkflowStage(
      trace,
      "FINAL_DECISION"
    );

  const sourceComplete =
    String(
      input?.status ||
      ""
    ).toUpperCase() ===
    "COMPLETE";

  const completedByLoadevo =
    String(
      intelligence?.status ||
      ""
    ).toUpperCase() ===
      "SKIPPED" &&
    String(
      intelligence?.reason ||
      ""
    ).toUpperCase() ===
      "ADDRESS_COMPLETE_AFTER_PREVIOUS_STAGE";

  const loadevoExecuted =
    Boolean(
      loadevo
    ) &&
    String(
      loadevo?.status ||
      ""
    ).toUpperCase() !==
      "SKIPPED";

  const countryDecisionReached =
    !sourceComplete &&
    !completedByLoadevo &&
    Boolean(
      registry ||
      web
    );

  const isSpain =
    String(
      point.country ||
      ""
    )
      .trim()
      .toUpperCase() ===
      "ES";

  const completedByRegistry =
    String(
      web?.status ||
      ""
    ).toUpperCase() ===
      "SKIPPED" &&
    String(
      web?.reason ||
      ""
    ).toUpperCase() ===
      "ADDRESS_COMPLETED_BY_REGISTRY";

  const registryExecuted =
    Boolean(
      registry
    ) &&
    String(
      registry?.status ||
      ""
    ).toUpperCase() !==
      "SKIPPED";

  const webExecuted =
    Boolean(
      web
    ) &&
    ![
      "",
      "SKIPPED",
    ].includes(
      String(
        web?.status ||
        ""
      ).toUpperCase()
    );

  const completedByWeb =
    String(
      web?.status ||
      ""
    ).toUpperCase() ===
      "MATCH" &&
    Boolean(
      geocoder
    ) &&
    String(
      geocoder?.status ||
      ""
    ).toUpperCase() !==
      "SKIPPED";

  const geocoderMatched =
    String(
      geocoder?.status ||
      ""
    ).toUpperCase() ===
      "MATCH";

  const resolved =
    String(
      finalDecision?.status ||
      ""
    ).toUpperCase() ===
      "RESOLVED" ||
    Boolean(
      String(
        point.coordinates ||
        ""
      ).trim()
    );

  const route = [
    "LUNA",
  ];

  if (sourceComplete) {
    route.push(
      "morada completa",
      "ignorar placeName",
      "geocode"
    );
  } else {
    route.push(
      "morada incompleta",
      "LOADEvo"
    );

    if (!completedByLoadevo) {
      if (isSpain) {
        route.push(
          "DB espanhola"
        );
      }

      if (!completedByRegistry) {
        route.push(
          "Internet"
        );
      }

      if (
        completedByRegistry ||
        completedByWeb
      ) {
        route.push(
          "geocode"
        );
      }
    } else {
      route.push(
        "geocode"
      );
    }
  }

  route.push(
    resolved
      ? "coordenadas"
      : 'COORDS=""'
  );

  return (
    <div className="coordinate-workflow">
      <div className="coordinate-workflow-topbar">
        <div>
          <strong className="coordinate-workflow-title">
            Workflow de resolução de coordenadas
          </strong>

          <span className="coordinate-workflow-subtitle">
            Caminho real executado por stop
          </span>
        </div>

        <div className="coordinate-workflow-stop-tabs">
          {stops.map(
            (stop) => (
              <button
                key={stop.key}
                type="button"
                className={[
                  "coordinate-workflow-stop-tab",
                  stop.key ===
                  selected.key
                    ? "is-active"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() =>
                  setSelectedKey(
                    stop.key
                  )
                }
              >
                <span>
                  {stop.type ===
                  "PICKUP"
                    ? "Carga"
                    : "Descarga"}{" "}
                  {stop.index + 1}
                </span>

                <small>
                  {stop.point
                    ?.location ||
                    "Stop"}
                </small>
              </button>
            )
          )}
        </div>
      </div>

      <div className="coordinate-workflow-selected-stop">
        <strong>
          {point.location ||
            "Stop"}
        </strong>

        <span>
          {[
            point.address,
            point.postalCode,
            point.city,
            point.country,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>

      <div className="coordinate-workflow-grid">
        <div className="coordinate-workflow-column">
          <WorkflowNode
            active
          >
            <strong>
              LUNA
            </strong>
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active
          >
            identifica o stop
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active
          >
            <strong>
              TEMOS:
            </strong>

            <span>
              rua/morada
            </span>

            <b>+</b>

            <span>
              número
            </span>

            <b>+</b>

            <span>
              cidade
            </span>

            <b>+</b>

            <span>
              país
            </span>

            <strong>
              ?
            </strong>
          </WorkflowNode>
        </div>

        <div
          className={[
            "coordinate-workflow-column",
            "coordinate-workflow-column--branch",
            sourceComplete
              ? "is-selected"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <WorkflowBranch
            type="yes"
            active={
              sourceComplete
            }
          >
            SIM
          </WorkflowBranch>

          <WorkflowArrow />

          <WorkflowNode
            active={
              sourceComplete
            }
          >
            IGNORAR{" "}
            <code>
              placeName
            </code>
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active={
              sourceComplete
            }
          >
            GEOCODE DA MORADA
          </WorkflowNode>

          <WorkflowArrow />

          <div className="coordinate-workflow-two-way">
            <div>
              <WorkflowBranch
                type="yes"
                active={
                  sourceComplete &&
                  geocoderMatched
                }
              >
                EXATO
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                success={
                  sourceComplete &&
                  resolved
                }
                active={
                  sourceComplete &&
                  resolved
                }
              >
                coordenadas
              </WorkflowNode>
            </div>

            <div>
              <WorkflowBranch
                type="no"
                active={
                  sourceComplete &&
                  !geocoderMatched
                }
              >
                NÃO EXATO
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                warning={
                  sourceComplete &&
                  !resolved
                }
                active={
                  sourceComplete &&
                  !resolved
                }
              >
                vazio
              </WorkflowNode>
            </div>
          </div>
        </div>

        <div
          className={[
            "coordinate-workflow-column",
            "coordinate-workflow-column--branch",
            !sourceComplete
              ? "is-selected"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <WorkflowBranch
            type="no"
            active={
              !sourceComplete
            }
          >
            NÃO
          </WorkflowBranch>

          <WorkflowArrow />

          <WorkflowNode
            active={
              loadevoExecuted
            }
          >
            <strong>
              PROCURAR NA LOADEvo
            </strong>
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active={
              Boolean(
                loadevoFill
              )
            }
          >
            preencher{" "}
            <strong>
              SÓ
            </strong>{" "}
            campos físicos em falta
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active={
              loadevoExecuted
            }
          >
            morada completa?
          </WorkflowNode>

          <div className="coordinate-workflow-two-way">
            <div>
              <WorkflowBranch
                type="yes"
                active={
                  completedByLoadevo
                }
              >
                SIM
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                active={
                  completedByLoadevo
                }
              >
                GEOCODE
              </WorkflowNode>
            </div>

            <div>
              <WorkflowBranch
                type="no"
                active={
                  loadevoExecuted &&
                  !completedByLoadevo
                }
              >
                NÃO
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                active={
                  countryDecisionReached
                }
              >
                ESPANHA?
              </WorkflowNode>
            </div>
          </div>
        </div>

        <div
          className={[
            "coordinate-workflow-column",
            "coordinate-workflow-column--branch",
            countryDecisionReached
              ? "is-selected"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <WorkflowNode
            active={
              countryDecisionReached
            }
          >
            <strong>
              ESPANHA?
            </strong>
          </WorkflowNode>

          <div className="coordinate-workflow-two-way">
            <div>
              <WorkflowBranch
                type="yes"
                active={
                  countryDecisionReached &&
                  isSpain
                }
              >
                SIM
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                active={
                  registryExecuted &&
                  isSpain
                }
              >
                DB espanhola

                <small>
                  PRTR
                </small>
              </WorkflowNode>

              <WorkflowArrow />

              <WorkflowNode
                active={
                  Boolean(
                    registryFill
                  )
                }
              >
                preencher campos físicos em falta
              </WorkflowNode>
            </div>

            <div>
              <WorkflowBranch
                type="no"
                active={
                  countryDecisionReached &&
                  !isSpain
                }
              >
                NÃO
              </WorkflowBranch>
            </div>
          </div>

          <WorkflowArrow />

          <WorkflowNode
            active={
              registryExecuted
            }
          >
            morada completa?
          </WorkflowNode>

          <div className="coordinate-workflow-two-way">
            <div>
              <WorkflowBranch
                type="yes"
                active={
                  completedByRegistry
                }
              >
                SIM
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                active={
                  completedByRegistry
                }
              >
                GEOCODE
              </WorkflowNode>
            </div>

            <div>
              <WorkflowBranch
                type="no"
                active={
                  countryDecisionReached &&
                  !completedByRegistry
                }
              >
                NÃO
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                active={
                  webExecuted
                }
              >
                INTERNET
              </WorkflowNode>
            </div>
          </div>
        </div>

        <div
          className={[
            "coordinate-workflow-column",
            "coordinate-workflow-column--branch",
            webExecuted
              ? "is-selected"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <WorkflowNode
            active={
              webExecuted
            }
          >
            <strong>
              INTERNET
            </strong>
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active={
              webExecuted
            }
          >
            <span>
              company website
            </span>

            <span>
              registry
            </span>

            <span>
              business pages
            </span>

            <span>
              directories
            </span>
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active={
              Boolean(
                webFill
              )
            }
          >
            preencher campos físicos em falta
          </WorkflowNode>

          <WorkflowArrow />

          <WorkflowNode
            active={
              webExecuted
            }
          >
            morada completa?
          </WorkflowNode>

          <div className="coordinate-workflow-two-way">
            <div>
              <WorkflowBranch
                type="yes"
                active={
                  completedByWeb
                }
              >
                SIM
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                active={
                  completedByWeb
                }
              >
                GEOCODE
              </WorkflowNode>
            </div>

            <div>
              <WorkflowBranch
                type="no"
                active={
                  webExecuted &&
                  !completedByWeb
                }
              >
                NÃO
              </WorkflowBranch>

              <WorkflowArrow />

              <WorkflowNode
                warning={
                  webExecuted &&
                  !completedByWeb
                }
                active={
                  webExecuted &&
                  !completedByWeb
                }
              >
                COORDS=""
              </WorkflowNode>
            </div>
          </div>
        </div>
      </div>

      <div className="coordinate-workflow-result">
        <div>
          <span>
            Caminho executado
          </span>

          <strong>
            {route.join(
              " → "
            )}
          </strong>
        </div>

        <div>
          <span>
            Resultado
          </span>

          <strong
            className={
              resolved
                ? "is-resolved"
                : "is-unresolved"
            }
          >
            {resolved
              ? point.coordinates
              : 'COORDS=""'}
          </strong>
        </div>
      </div>
    </div>
  );
};

const CreateLoad = () => {
  const { t, i18n } = useTranslation("create_load");

  const [loading, setLoading] = useState(false);
  const [initializingSession, setInitializingSession] = useState(false);
  const [message, setMessage] = useState("");
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  const [
    aiImportJob,
    setAiImportJob,
  ] = useState(null);

  const [
    coordinateWorkflowStops,
    setCoordinateWorkflowStops,
  ] = useState([]);

  const activeAiImportRunRef = useRef(0);

  const aiJourneyRef = useRef(null);
  const loadDetailsRef = useRef(null);
  const [aiJourneyScrollRun, setAiJourneyScrollRun] = useState(0);

  const [stopOpenRequest, setStopOpenRequest] = useState({
    kind: null,
    index: -1,
    seq: 0,
  });

  const [atlasCollapsed, setAtlasCollapsed] = useState(false);

  useEffect(() => {
    if (!aiJourneyScrollRun) return;

    const timer = window.setTimeout(() => {
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      const journey = aiJourneyRef.current;
      if (!journey) return;

      const behavior = reducedMotion ? "auto" : "smooth";

      if (window.matchMedia("(max-width: 1250px)").matches) {
        journey.scrollIntoView({
          behavior,
          block: "start",
        });
      } else {
        journey.closest(".cl-route-preview")?.scrollTo({
          top: 0,
          behavior,
        });
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [aiJourneyScrollRun]);

  const [currentLoad, setCurrentLoad] = useState({
    transportsessionId: null,
    id: "TBD",
  });
  const [lengthInput, setLengthInput] = useState("");
  const [totalUsedLength, setTotalUsedLength] = useState(0);
  const [totalUsedHeight, setTotalUsedHeight] = useState(0);
  const [totalUsedWidth, setTotalUsedWidth] = useState(0);
  const [availableHeight, setAvailableHeight] = useState(2.7);
  const [availableWidth, setAvailableWidth] = useState(2.5);
  const [heightInput, setHeightInput] = useState("");
  const [widthInput, setWidthInput] = useState("");
  const [weightInput, setWeightInput] = useState("");
  const [industryClientId, setIndustryClientId] = useState("");
  const [carrierClientId, setCarrierClientId] = useState("");
  const [industryClients, setIndustryClients] = useState([]);
  const [carrierClients, setCarrierClients] = useState([]);
  const [loadBearer, setLoadBearer] = useState(false);
  const [error, setError] = useState("");
  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");
  const [specialDimError, setSpecialDimError] = useState("");
  const [weightError, setWeightError] = useState("");
  const [merchandise, setMerchandise] = useState("");
  const [merchandiseError, setMerchandiseError] = useState("");

  // 🆕 Trailer Type (load-level)
  const [trailerType, setTrailerType] = useState(null);       // e.g. "FRIGO" | "TAUTLINER" | "OTHER"
  const [trailerTypeOther, setTrailerTypeOther] = useState(""); // free text when OTHER
  const [trailerTypeError, setTrailerTypeError] = useState("");
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState("");

  // 💰 Price
  const [priceInput, setPriceInput] = useState("");
  const [priceCurrency, setPriceCurrency] = useState("EUR");
  const [priceError, setPriceError] = useState("");

  // 📝 Notes for driver (optional)
  const [notes4driver, setNotes4driver] = useState("");
  const [notes4driverError, setNotes4driverError] = useState("");

  // 🆕 Notes for subcontracted (optional, only when external carrier selected)
  const [notes4subcontracted, setNotes4subcontracted] = useState("");
  const [notes4subcontractedError, setNotes4subcontractedError] = useState("");

  // 🆕 Subcontracted Emails (only when external carrier selected, max 3)
  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState([]);

  // 🆕 Price for subcontracted (optional, only when external carrier selected)
  const [price4subcontractedInput, setPrice4subcontractedInput] = useState("");
  const [price4subcontractedError, setPrice4subcontractedError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const [existingDateHourConflicts, setExistingDateHourConflicts] = useState([]);
  const [conflictDateHours, setConflictDateHours] = useState(new Set());
  const handleCancel = () => navigate("/loads");

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads");
  };

  const [pickUpPlaces, setPickUpPlaces] = useState(() => [
    {
      _uiId: createStopUiId(),
      location: "",
      date: "",
      hour: "",
      scheduleType: "FIXED",
      hourEnd: null,

      // 🆕 point-level dimensions
      length: "",
      width: "",
      height: "",

      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",
      workingHours: null,
      placeId: null,
      isNewPlace: false, // 🆕 typed manually in CreatableSelect
      loadingAccess: "SIDE", // ✅ default: Lateral
      handlingEquipment: "FORKLIFT", // ✅ default: Empilhador
      handlingEquipmentOther: "", // 🆕 per-point
      reference: "", // 🆕 NEW per-point (optional)
      showMoreFields: true,
      locationError: false,
      dateError: false,
      hourError: false,
      addressError: false,
      postalCodeError: false,
      cityError: false,
      countryError: false,
      gMapsLinkError: false,
      coordinatesError: false,
      contactPhoneError: false,
    },
  ]);

  const [activePickUpIndex, setActivePickUpIndex] = useState(0);

  const [deliveryPlaces, setDeliveryPlaces] = useState(() => [
    {
      _uiId: createStopUiId(),
      location: "",
      date: "",
      hour: "",
      scheduleType: "FIXED",
      hourEnd: null,

      // 🆕 point-level dimensions
      length: "",
      width: "",
      height: "",

      placeId: null,
      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",
      workingHours: null,
      isNewPlace: false, // 🆕 typed manually in CreatableSelect
      loadingAccess: "SIDE", // ✅ default: Lateral
      handlingEquipment: "FORKLIFT", // ✅ default: Empilhador
      handlingEquipmentOther: "", // 🆕 per-point
      reference: "", // 🆕 NEW per-point (optional)
      showMoreFields: true,
      hourError: false,
    },
  ]);

  const [activeDeliveryIndex, setActiveDeliveryIndex] = useState(0);

  // STOP REORDER — pickup and delivery remain independent.
  const dragStopRef = useRef(null);
  const stopPositionsRef = useRef(null);

  const [draggingStop, setDraggingStop] = useState(null);
  const [sequenceAlert, setSequenceAlert] = useState(null);
  const [scheduleEditor, setScheduleEditor] = useState(null);

  const stopDndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 7 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fixed Time / Time Window
  const [timeSchedulePopup, setTimeSchedulePopup] = useState(null);

  const openTimeSchedule = (kind, point, element) => {
    const rect = element.getBoundingClientRect();
    const width = Math.min(390, window.innerWidth - 24);
    const desiredHeight = 325;

    const left = Math.max(
      12,
      Math.min(
        rect.right - width,
        window.innerWidth - width - 12
      )
    );

    const top =
      rect.bottom + desiredHeight + 12 < window.innerHeight
        ? rect.bottom + 8
        : Math.max(12, rect.top - desiredHeight - 8);

    setTimeSchedulePopup({
      kind,
      id: point._uiId,
      top,
      left,
      scheduleType:
        point.scheduleType === "WINDOW" ? "WINDOW" : "FIXED",
      hour: point.hour || "",
      hourEnd: point.hourEnd || "",
    });
  };

  const applyTimeSchedule = ({ scheduleType, hour, hourEnd }) => {
    if (!timeSchedulePopup) return;

    const { kind, id } = timeSchedulePopup;

    const rows =
      kind === "pickup" ? pickUpPlaces : deliveryPlaces;

    const index = rows.findIndex(
      (point) => point._uiId === id
    );

    if (index < 0) {
      setTimeSchedulePopup(null);
      return;
    }

    const setter =
      kind === "pickup" ? setPickUpPlaces : setDeliveryPlaces;

    setter((current) =>
      current.map((point) =>
        point._uiId === id
          ? {
              ...point,
              scheduleType,
              hour,
              hourEnd,
              hourError: false,
            }
          : point
      )
    );

    setInvalidFields((current) => {
      const copy = { ...current };
      delete copy[`${kind}-hour-${index}`];
      return copy;
    });

    setGlobalHourFormatError(false);
    setTimeSchedulePopup(null);
  };

  const reorderLocked = Boolean(
    aiImportJob &&
    !["COMPLETED", "FAILED"].includes(
      String(aiImportJob.status || "").toUpperCase()
    )
  );

  const captureStopPositions = (kind) => {
    const positions = new Map();

    document.querySelectorAll(
      `.loadevo-v6-stop-mount[data-stop-kind="${kind}"]`
    ).forEach((node) => {
      positions.set(
        node.dataset.stopId,
        node.getBoundingClientRect().top
      );
    });

    stopPositionsRef.current = { kind, positions };
  };

  // Animate card movement after React updates the order.
  React.useLayoutEffect(() => {
    const previous = stopPositionsRef.current;

    if (!previous) return;

    stopPositionsRef.current = null;

    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    document.querySelectorAll(
      `.loadevo-v6-stop-mount[data-stop-kind="${previous.kind}"]`
    ).forEach((node) => {
      const oldTop = previous.positions.get(node.dataset.stopId);

      if (oldTop === undefined) return;

      const offset = oldTop - node.getBoundingClientRect().top;

      if (
        Math.abs(offset) < 1 ||
        typeof node.animate !== "function"
      ) {
        return;
      }

      node.animate(
        [
          { transform: `translateY(${offset}px)` },
          { transform: "translateY(0)" },
        ],
        {
          duration: 260,
          easing: "cubic-bezier(.2,.8,.2,1)",
        }
      );
    });
  }, [pickUpPlaces, deliveryPlaces]);

  const applyStopOrder = (kind, before, after) => {
    // dnd-kit animates the movement; FLIP remains for keyboard/revert only.
    if (!dragStopRef.current) {
      captureStopPositions(kind);
    }

    if (kind === "pickup") {
      setPickUpPlaces(after);
    } else {
      setDeliveryPlaces(after);
    }

    setInvalidFields((old) =>
      remapStopErrors(old, kind, before, after, true)
    );

    setDateFieldErrors((old) =>
      remapStopErrors(old, kind, before, after, false)
    );

    setStopOpenRequest((old) => {
      if (old.kind !== kind || old.index < 0) {
        return old;
      }

      const previousId = before[old.index]?._uiId;
      const updatedIndex = after.findIndex(
        (stop) => stop._uiId === previousId
      );

      return {
        ...old,
        index: updatedIndex,
      };
    });
  };

  const reorderStop = (kind, movingId, targetId) => {
    if (reorderLocked) return;

    const before =
      kind === "pickup" ? pickUpPlaces : deliveryPlaces;

    const from = before.findIndex(
      (stop) => stop._uiId === movingId
    );

    const to = before.findIndex(
      (stop) => stop._uiId === targetId
    );

    if (from < 0 || to < 0 || from === to) {
      return;
    }

    const after = [...before];

    after.splice(to, 0, after.splice(from, 1)[0]);

    applyStopOrder(kind, before, after);

    const conflict = findStopSequenceConflict(after);

    setSequenceAlert(
      conflict
        ? {
            kind,
            movingId,
            previousOrder: before.map((stop) => stop._uiId),
          }
        : null
    );

    setScheduleEditor(null);
  };

  const revertStopOrder = () => {
    if (!sequenceAlert) return;

    const { kind, previousOrder } = sequenceAlert;

    const before =
      kind === "pickup" ? pickUpPlaces : deliveryPlaces;

    const ranks = new Map(
      previousOrder.map((id, index) => [id, index])
    );

    const after = [...before].sort(
      (a, b) =>
        (ranks.get(a._uiId) ?? Number.MAX_SAFE_INTEGER) -
        (ranks.get(b._uiId) ?? Number.MAX_SAFE_INTEGER)
    );

    applyStopOrder(kind, before, after);

    setSequenceAlert(null);
    setScheduleEditor(null);
  };

  const openScheduleEditor = () => {
    if (!sequenceAlert) return;

    const points =
      sequenceAlert.kind === "pickup"
        ? pickUpPlaces
        : deliveryPlaces;

    const issue = findStopSequenceConflict(points);

    if (!issue) return;

    const first = points[issue.beforeIndex];
    const second = points[issue.afterIndex];

    const moved = [first, second].find(
      (point) => point._uiId === sequenceAlert.movingId
    );

    const selected = moved || second;

    setScheduleEditor({
      kind: sequenceAlert.kind,
      id: selected._uiId,
      date: selected.date || "",
      hour: selected.hour || "",
      error: "",
    });
  };

  const chooseScheduleStop = (id) => {
    if (!scheduleEditor) return;

    const points =
      scheduleEditor.kind === "pickup"
        ? pickUpPlaces
        : deliveryPlaces;

    const issue = findStopSequenceConflict(points);

    if (!issue) return;

    const selected = [
      points[issue.beforeIndex],
      points[issue.afterIndex],
    ].find((point) => point._uiId === id);

    if (!selected) return;

    setScheduleEditor((previous) => ({
      ...previous,
      id: selected._uiId,
      date: selected.date || "",
      hour: selected.hour || "",
      error: "",
    }));
  };

  const saveScheduleEditor = () => {
    if (!scheduleEditor) return;

    const { kind, id, date, hour } = scheduleEditor;

    if (!stopScheduleValue({ date, hour })) {
      setScheduleEditor((old) => ({
        ...old,
        error: "Enter a valid date and time.",
      }));
      return;
    }

    const before =
      kind === "pickup" ? pickUpPlaces : deliveryPlaces;

    const stopIndex = before.findIndex(
      (point) => point._uiId === id
    );

    if (stopIndex === -1) return;

    const selectedBefore = before[stopIndex];

    if (
      selectedBefore?.scheduleType === "WINDOW" &&
      selectedBefore.hourEnd &&
      hour >= selectedBefore.hourEnd
    ) {
      setScheduleEditor((old) => ({
        ...old,
        error:
          "The start of the time window must be earlier than its end. " +
          "Use the schedule picker to adjust both times.",
      }));

      return;
    }

    const after = before.map((point) =>
      point._uiId === id
        ? {
            ...point,
            date,
            hour,
            scheduleType: point.scheduleType || "FIXED",
            hourEnd: point.hourEnd || null,
            hourError: false,
          }
        : point
    );

    if (findStopSequenceConflict(after)) {
      setScheduleEditor((old) => ({
        ...old,
        error:
          "The new time still conflicts with the order. Check the previous and next stops.",
      }));
      return;
    }

    if (kind === "pickup") {
      setPickUpPlaces(after);
    } else {
      setDeliveryPlaces(after);
    }

    setInvalidFields((old) => {
      const next = { ...old };

      delete next[`${kind}-date-${stopIndex}`];
      delete next[`${kind}-hour-${stopIndex}`];

      return next;
    });

    setDateFieldErrors((old) => {
      const next = { ...old };

      delete next[`${kind}-${stopIndex}`];

      return next;
    });

    setSequenceAlert(null);
    setScheduleEditor(null);
  };

  const renderSequenceAlert = (kind) => {
    if (sequenceAlert?.kind !== kind) return null;

    const points =
      kind === "pickup" ? pickUpPlaces : deliveryPlaces;

    const issue = findStopSequenceConflict(points);

    if (!issue) return null;

    const earlier = points[issue.beforeIndex];
    const later = points[issue.afterIndex];

    return (
      <div className="loadevo-reorder-alert" role="status">
        <FiClock aria-hidden="true" />

        <div className="loadevo-reorder-alert-copy">
          <strong>
            Schedule conflict · {kind === "pickup" ? "Pickups" : "Deliveries"}
          </strong>

          <span>
            #{issue.beforeIndex + 1} {earlier.location || "Unnamed stop"}
            {" "}({formatReorderSchedule(earlier)}) comes before{" "}
            #{issue.afterIndex + 1} {later.location || "Unnamed stop"}
            {" "}({formatReorderSchedule(later)}), but is scheduled later.
          </span>
        </div>

        <div className="loadevo-reorder-alert-actions">
          <button
            type="button"
            onClick={revertStopOrder}
          >
            Revert move
          </button>

          <button
            type="button"
            className="is-primary"
            onClick={openScheduleEditor}
          >
            Adjust schedule
          </button>
        </div>
      </div>
    );
  };

  const startStopDrag = ({ active }, kind) => {
    if (reorderLocked) return;

    const id = String(active.id);
    dragStopRef.current = { kind, id };
    setDraggingStop({ kind, id });
  };

  const finishStopDrag = () => {
    dragStopRef.current = null;
    setDraggingStop(null);
  };

  const dropOnStop = ({ active, over }, kind) => {
    const moving = dragStopRef.current;

    if (
      moving?.kind === kind &&
      !reorderLocked &&
      over &&
      String(active.id) !== String(over.id)
    ) {
      reorderStop(kind, moving.id, String(over.id));
    }

    finishStopDrag();
  };

  const nudgeStopWithKeyboard = (event, kind, id, index) => {
    if (
      event.key !== "ArrowUp" &&
      event.key !== "ArrowDown"
    ) {
      return;
    }

    event.preventDefault();

    if (reorderLocked) return;

    const items =
      kind === "pickup" ? pickUpPlaces : deliveryPlaces;

    const nextIndex =
      index + (event.key === "ArrowUp" ? -1 : 1);

    if (nextIndex < 0 || nextIndex >= items.length) {
      return;
    }

    reorderStop(kind, id, items[nextIndex]._uiId);
  };

  const [pickUpPlaceError, setPickUpPlaceError] = useState("");
  const [deliveryPlaceError, setDeliveryPlaceError] = useState("");
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [dateErrorModal, setDateErrorModal] = useState({ visible: false, type: "", index: null });

  // 🆕 Ask user if newly typed places should be saved into Places
  const [saveNewPlacesModal, setSaveNewPlacesModal] = useState({
    visible: false,
    candidates: [],
    payload: null,
    saving: false,
  });

  // Track the actual current stops; never count a pending location
  // as geographically verified merely because text was extracted.
  useEffect(() => {
    if (!aiImportJob || !["LOCATION_RESOLUTION", "COMPLETED"].includes(
      String(aiImportJob.stage || "").toUpperCase()
    )) return;
    setCoordinateWorkflowStops([
      ...pickUpPlaces.map((point, index) => ({
        key: `pickup-${index}`, type: "PICKUP", index, point,
      })),
      ...deliveryPlaces.map((point, index) => ({
        key: `delivery-${index}`, type: "DELIVERY", index, point,
      })),
    ]);
  }, [aiImportJob?.stage, pickUpPlaces, deliveryPlaces]);

  const params = new URLSearchParams(location.search);
  const rawSessionId = params.get("transportsessionId");
  const existingTransportSessionId = rawSessionId && rawSessionId !== "null" ? rawSessionId : null;
  const maxAllowedLength = parseFloat(params.get("maxLength")) || 13.6;
  const maxAllowedHeight = 2.7;
  const maxAllowedWidth = 2.5;

  const dateRefs = useRef([]);
  const deliveryDateRefs = useRef([]);

  const [globalHourFormatError, setGlobalHourFormatError] = useState(false);
  const [clientType, setClientType] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState([]);
  const [specialLoad, setSpecialLoad] = useState(false);
  const [invalidFields, setInvalidFields] = useState({});
  const [transportCarrierId, setTransportCarrierId] = useState(null);
  const [availableCarriers, setAvailableCarriers] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [carrierError, setCarrierError] = useState(false);
    // 🆕 selected carrier option (from dropdown)
  const selectedTransportOptMemo = useMemo(() => {
    return (availableCarriers || []).find((c) => String(c.value) === String(transportCarrierId)) || null;
  }, [availableCarriers, transportCarrierId]);

  // 🆕 your internal carrier is the "__internal__" option you build
  const isInternalCarrierMemo = useMemo(() => {
    return selectedTransportOptMemo?.value === "__internal__";
  }, [selectedTransportOptMemo]);

  // 🆕 show notes4subcontracted ONLY if carrier is external (not internal)
  // and (optionally) the selected carrier name differs from your companyName
  const showNotes4subcontracted = useMemo(() => {
    if (!transportCarrierId) return false;
    if (isInternalCarrierMemo) return false;

    const own = String(companyName || "").trim().toLowerCase();
    const sel = String(selectedTransportOptMemo?.label || "").trim().toLowerCase();

    if (!own || !sel) return true; // if can't compare, still treat as external
    return sel !== own;
  }, [transportCarrierId, isInternalCarrierMemo, companyName, selectedTransportOptMemo]);

  // 🧼 if user switches back to internal carrier, clear subcontracted fields
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setNotes4subcontracted("");
      setNotes4subcontractedError("");
      setSelectedSubcontractedEmails([]);

      setPrice4subcontractedInput("");
      setPrice4subcontractedError("");
    }
  }, [showNotes4subcontracted]);


  const [clientTypeError, setClientTypeError] = useState(false);
  const [selectedClientError, setSelectedClientError] = useState(false);
  const [dateFieldErrors, setDateFieldErrors] = useState({});
  const [allPlaces, setAllPlaces] = useState([]);
  const [showPickupHiddenFields, setShowPickupHiddenFields] = useState(true);
  const [invalidPickupFields, setInvalidPickupFields] = useState({});

  const normalizeMoney = (str) => {
    if (str == null) return "";
    const s = String(str).trim().replace(/\s+/g, "");
    const hasDot = s.includes(".");
    const hasComma = s.includes(",");
    let t2 = s.replace(/[^\d.,-]/g, "");
    if (hasDot && hasComma) {
      const last = Math.max(t2.lastIndexOf("."), t2.lastIndexOf(","));
      t2 = t2
        .split("")
        .filter((ch, i) => (ch === "." || ch === ",") ? i === last : true)
        .join("");
    }
    t2 = t2.replace(",", ".");
    return t2;
  };

  const formatPrice = (amount, currency = "EUR") => {
    if (amount == null || amount === "") return "";
    const num = Number(amount);
    if (Number.isNaN(num)) return String(amount);
    const locale = (i18n.language || "en").includes("-") ? i18n.language : undefined;
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format(num);
    } catch {
      return `${num.toFixed(2)} ${currency}`;
    }
  };

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  };

  const extractClientEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];

    const found = new Set();

    const addEmail = (value) => {
      const email = String(value || "").trim().toLowerCase();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email);
    };

    const contactsRaw = clientObj.contacts;
    let contacts = [];

    if (Array.isArray(contactsRaw)) {
      contacts = contactsRaw;
    } else if (typeof contactsRaw === "string") {
      try {
        const parsed = JSON.parse(contactsRaw);
        if (Array.isArray(parsed)) contacts = parsed;
      } catch {
        contacts = [];
      }
    }

    contacts.forEach((contact) => {
      addEmail(contact?.contactEmail);
    });

    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType || !selectedClient?.value) return [];
  
    const sourceList = clientType === "industry" ? industryClients : carrierClients;
    const fullClient = (sourceList || []).find((c) => String(c.id) === String(selectedClient.value));
  
    return extractClientEmails(fullClient);
  }, [clientType, selectedClient, industryClients, carrierClients]);


    const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showNotes4subcontracted || !transportCarrierId) return [];

    const fullCarrier = (carrierClients || []).find(
      (c) => String(c.id) === String(transportCarrierId)
    );

    return extractClientEmails(fullCarrier);
  }, [showNotes4subcontracted, transportCarrierId, carrierClients]);

  /* ==== Working Hours helpers ==== */
  const DAYS_NUM_TO_KEY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  function weekdayKeyFromISO(isoDateStr) {
    if (!isoDateStr) return null;
    const d = new Date(isoDateStr);
    if (Number.isNaN(d)) return null;
    return DAYS_NUM_TO_KEY[d.getDay()];
  }

  function parseWorkingHours(wh) {
    if (!wh) return null;
    if (typeof wh === "string") {
      try {
        return JSON.parse(wh);
      } catch {
        return null;
      }
    }
    if (typeof wh === "object") return wh;
    return null;
  }

  function formatIntervalsForDay(workingHours, dayKey) {
    if (!workingHours || !dayKey) return null;
    const day = workingHours[dayKey];
    if (!Array.isArray(day)) return null;
    if (day.length === 0) return t("workingHours.closed");
    return day.map(([a, b]) => `${a}–${b}`).join(", ");
  }

  const renderStopLocationOption = (opt, context, place) => {
    const isSelectedValue = context === "value";

    const dayKey = weekdayKeyFromISO(place.date);
    const workingHours = parseWorkingHours(place.workingHours);
    const hoursText = formatIntervalsForDay(workingHours, dayKey);

    const showHours = isSelectedValue && dayKey && hoursText;

    const city = opt.metaCity || (isSelectedValue ? place.city : "");
    const country = opt.metaCountry || (isSelectedValue ? place.country : "");

    return (
      <div className="loadevo-v8-location-option">
        <div className="loadevo-v8-location-option-heading">
          <span className="loadevo-v8-location-option-name">
            {opt.label}
          </span>

          {showHours && (
            <span
              className="loadevo-v8-location-working-hours"
              title={`${dayKey} ${hoursText}`}
            >
              <FiClock aria-hidden="true" />

              <strong>{dayKey}</strong>

              <span>{hoursText}</span>
            </span>
          )}
        </div>

        {city && (
          <div className="loadevo-v8-location-option-address">
            {city}
            {country ? `, ${country}` : ""}
          </div>
        )}
      </div>
    );
  };


  const chooseGoogleGeoCandidate = (kind, index, selected) => {
    const setter = kind === "pickup" ? setPickUpPlaces : setDeliveryPlaces;
    setter(previous => previous.map((point, i) => {
      if (i !== index) return point;

      const candidateOptions =
        Array.isArray(point.aiGeocode?.candidateOptions)
          ? point.aiGeocode.candidateOptions : [];

      const suggestionOptions =
        Array.isArray(point.aiGeocode?.suggestionOptions)
          ? point.aiGeocode.suggestionOptions : [];

      const chosen = [...candidateOptions, ...suggestionOptions].find(
        option => option.coordinates === selected?.coordinates &&
                  option.gMapsLink === selected?.gMapsLink
      );
      if (!chosen) return point;

      return {
        ...point,
        coordinates: "",
        gMapsLink: "",
        aiGeocode: {
          ...point.aiGeocode,
          proposedLocation: {
            ...chosen,
            conflicts: Array.isArray(chosen.conflicts)
              ? chosen.conflicts
              : suggestionOptions.includes(chosen)
                ? ["INSUFFICIENT_EVIDENCE"]
                : ["MULTIPLE_CANDIDATES"],
            reviewStatus: "PENDING",
            verified: false,
            requiresReview: true
          }
        }
      };
    }));
  };

  const confirmGoogleGeoProposal = (kind, index, proposed) => {
    const coordinates = proposed.coordinates;
    const setter = kind === "pickup" ? setPickUpPlaces : setDeliveryPlaces;

    setter(previous => previous.map((point, i) => {
      if (i !== index ||
          point.aiGeocode?.proposedLocation?.coordinates !== coordinates)
        return point;

      return {
        ...point,
        coordinates,
        gMapsLink: "https://www.google.com/maps?q=" +
          encodeURIComponent(coordinates),
        aiGeocode: {
          ...point.aiGeocode,
          proposedLocation: {
            ...proposed,
            reviewStatus: "CONFIRMED_BY_DISPATCHER",
            confirmedAt: new Date().toISOString()
          }
        }
      };
    }));

    setInvalidFields(previous => {
      const next = {...previous};
      delete next[`${kind}-coordinates-${index}`];
      delete next[`${kind}-gMapsLink-${index}`];
      return next;
    });
  };

  const getAiCoordinateExplanation = (place) => {
    if (
      !place ||
      String(
        place.coordinates || ""
      ).trim()
    ) {
      return "";
    }

    const geocode =
      place.aiGeocode ||
      null;

    const placeResolution =
      place.aiPlaceResolution ||
      null;

    const geocodeStatus =
      String(
        geocode?.status ||
        ""
      )
        .trim()
        .toUpperCase();

    const matchQuality =
      String(
        geocode?.matchQuality ||
        ""
      )
        .trim()
        .toUpperCase();

    const placeResolutionStatus =
      String(
        placeResolution?.status ||
        ""
      )
        .trim()
        .toUpperCase();

    const reasonCodes = [
      ...(
        Array.isArray(
          geocode?.reasonCodes
        )
          ? geocode.reasonCodes
          : []
      ),
      ...(
        Array.isArray(
          placeResolution?.reasonCodes
        )
          ? placeResolution.reasonCodes
          : []
      ),
    ].map((reason) =>
      String(
        reason || ""
      )
        .trim()
        .toUpperCase()
    );

    if (
      geocodeStatus === "ERROR"
    ) {
      return t(
        "aiImport.fieldExplanations.geocodeFailed",
        {
          defaultValue:
            "LOADEvo could not verify this location because the geographic lookup failed.",
        }
      );
    }

    if (
      reasonCodes.some(
        (reason) =>
          reason.includes(
            "CRITICAL_FIELD_MISMATCH"
          ) ||
          reason.includes(
            "CONFLICT"
          )
      )
    ) {
      return t(
        "aiImport.fieldExplanations.geocodeConflict",
        {
          defaultValue:
            "A possible geographic match was rejected because important address details did not agree with the transport order.",
        }
      );
    }

    if (
      reasonCodes.some(
        (reason) =>
          reason.includes(
            "POSTCODE_CENTROID"
          ) ||
          reason.includes(
            "CITY_CENTROID"
          ) ||
          reason.includes(
            "COUNTRY_CENTROID"
          ) ||
          reason.includes(
            "POSTCODE_FALLBACK"
          ) ||
          reason.includes(
            "CITY_FALLBACK"
          ) ||
          reason.includes(
            "COUNTRY_FALLBACK"
          )
      )
    ) {
      return t(
        "aiImport.fieldExplanations.geocodeTooImprecise",
        {
          defaultValue:
            "A geographic match was found, but it was too imprecise to use safely for an operational stop.",
        }
      );
    }

    if (
      geocode?.requiresReview === true ||
      matchQuality === "REVIEW"
    ) {
      return t(
        "aiImport.fieldExplanations.geocodeNeedsReview",
        {
          defaultValue:
            "A possible geographic match was found, but it was not reliable enough to fill the coordinates automatically.",
        }
      );
    }

    if (
      geocodeStatus === "NO_MATCH"
    ) {
      return t(
        "aiImport.fieldExplanations.geocodeNoMatch",
        {
          defaultValue:
            "LOADEvo could not find a sufficiently reliable geographic match for this location.",
        }
      );
    }

    if (
      placeResolutionStatus ===
      "AMBIGUOUS"
    ) {
      return t(
        "aiImport.fieldExplanations.placeAmbiguous",
        {
          defaultValue:
            "More than one possible place matched the document, so LOADEvo did not choose coordinates automatically.",
        }
      );
    }

    if (
      placeResolutionStatus ===
      "CONFLICT"
    ) {
      return t(
        "aiImport.fieldExplanations.placeConflict",
        {
          defaultValue:
            "The location information conflicts with an existing Place, so the coordinates were left for manual review.",
        }
      );
    }

    if (
      placeResolutionStatus ===
      "ERROR"
    ) {
      return t(
        "aiImport.fieldExplanations.placeResolutionFailed",
        {
          defaultValue:
            "LOADEvo could not safely resolve this location against the Places database.",
        }
      );
    }

    return "";
  };

  const getAiFieldExplanation = (
    place,
    field
  ) => {
    if (
      field === "coordinates"
    ) {
      return getAiCoordinateExplanation(
        place
      );
    }

    if (
      field === "gMapsLink"
    ) {
      if (
        String(
          place?.gMapsLink || ""
        ).trim()
      ) {
        return "";
      }

      const coordinateReason =
        getAiCoordinateExplanation(
          place
        );

      if (!coordinateReason) {
        return "";
      }

      return (
        coordinateReason +
        " " +
        t(
          "aiImport.fieldExplanations.gMapsNeedsCoordinates",
          {
            defaultValue:
              "A Google Maps link is only generated after the coordinates are verified.",
          }
        )
      );
    }

    return "";
  };

  const renderAiFieldExplanation = (
    place,
    field
  ) => {
    const explanation =
      getAiFieldExplanation(
        place,
        field
      );

    if (!explanation) {
      return null;
    }

    return (
      <div className="create-load-ai-field-explanation">
        <FiInfo className="create-load-ai-field-explanation-icon" />

        <span>
          {explanation}
        </span>
      </div>
    );
  };

  function placeKey(p) {
    const norm = (s) => String(s ?? "").trim().toLowerCase();
    return [norm(p.placeName), norm(p.address), norm(p.postalCode), norm(p.city), norm(p.country)].join("|");
  }
  /* ==== end helpers ==== */

  const placeOptions = useMemo(() => {
    const map = new Map();
    for (const p of allPlaces || []) {
      const k = placeKey(p);
      if (!map.has(k)) map.set(k, p);
    }
    const unique = Array.from(map.values());

    return unique
      .map((p) => ({
        value: String(p.id),
        label: p.placeName,
        metaCity: p.city || "",
        metaCountry: p.country || "",
        fullData: p,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [allPlaces]);

  const carrierSelectStyles = useMemo(() => {
    const baseStyles = customSelectStyles || {};

    return {
      ...baseStyles,
      option: (base, state) => {
        const prev = typeof baseStyles.option === "function" ? baseStyles.option(base, state) : base;

        return {
          ...prev,
          height: "auto",          // ✅ allow multi-line
          whiteSpace: "normal",    // ✅ allow wrap/new lines
          alignItems: "flex-start",
          paddingTop: 10,
          paddingBottom: 10,
        };
      },
      menuList: (base) => ({
        ...base,
        paddingTop: 4,
        paddingBottom: 4,
      }),
    };
  }, []);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ mantém as tags numa só linha
        flexWrap: "nowrap",

        // ✅ quando há várias, desliza horizontalmente
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        // ✅ mantém o alinhamento bonito
        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ reserva espaço para clear + seta
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),

        // ✅ cada tag mantém a sua largura e não fica esmagada
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),
    };
  }, []);

  const pointTypeLabel = (type) => {
    if (type === "PICKUP") return t("pointTypes.pickup");
    if (type === "DELIVERY") return t("pointTypes.delivery");
    if (type === "STRATEGIC") return t("pointTypes.strategic");
    return type || "";
  };

  const loadingAccessOptions = useMemo(() => ([
    { value: "SIDE", label: t("loadingAccess.side", { defaultValue: "Side" }) },
    { value: "REAR", label: t("loadingAccess.rear", { defaultValue: "Rear" }) },
    { value: "TOP", label: t("loadingAccess.top", { defaultValue: "Top" }) },
    { value: "DOCK", label: t("loadingAccess.dock", { defaultValue: "Dock" }) },
    { value: "ANY", label: t("loadingAccess.any", { defaultValue: "Any" }) },
  ]), [t]);

  const handlingEquipmentOptions = useMemo(() => ([
    { value: "FORKLIFT", label: t("handlingEquipment.forklift", { defaultValue: "Forklift" }) },
    { value: "PALLET_JACK", label: t("handlingEquipment.palletJack", { defaultValue: "Pallet jack" }) },
    { value: "ELECTRIC_PALLET_JACK", label: t("handlingEquipment.electricPalletJack", { defaultValue: "Electric pallet jack" }) },
    { value: "CRANE", label: t("handlingEquipment.crane", { defaultValue: "Crane" }) },
    { value: "LOADING_RAMP", label: t("handlingEquipment.loadingRamp", { defaultValue: "Loading ramp" }) },
    { value: "TAIL_LIFT", label: t("handlingEquipment.tailLift", { defaultValue: "Tail lift" }) },
    { value: "HAND_LOAD", label: t("handlingEquipment.handLoad", { defaultValue: "Hand load" }) },
    { value: "CONVEYOR", label: t("handlingEquipment.conveyor", { defaultValue: "Conveyor" }) },
    { value: "OTHER", label: t("handlingEquipment.other", { defaultValue: "Other" }) },
  ]), [t]);

  // 🆕 Trailer Type options (KEEP IN SYNC with backend isIn)
  const trailerTypeOptions = useMemo(() => ([
    { value: "ANY", label: t("trailerType.any", { defaultValue: "Any" }) },

    { value: "TAUTLINER", label: t("trailerType.tautliner", { defaultValue: "Tautliner / Curtain" }) },
    { value: "REEFER", label: t("trailerType.reefer", { defaultValue: "Reefer / Frigo" }) },
    { value: "BOX", label: t("trailerType.box", { defaultValue: "Box" }) },
    { value: "FLATBED", label: t("trailerType.flatbed", { defaultValue: "Flatbed" }) },
    { value: "LOWBED", label: t("trailerType.lowbed", { defaultValue: "Lowbed" }) },
    { value: "TANKER", label: t("trailerType.tanker", { defaultValue: "Tanker" }) },
    { value: "TIPPER", label: t("trailerType.tipper", { defaultValue: "Tipper" }) },
    { value: "CONTAINER_CHASSIS", label: t("trailerType.containerChassis", { defaultValue: "Container chassis" }) },
    { value: "MEGA", label: t("trailerType.mega", { defaultValue: "Mega" }) },

    { value: "OTHER", label: t("trailerType.other", { defaultValue: "Other" }) },
  ]), [t]);

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const res = await axios.get("/places");
        setAllPlaces(res.data);
      } catch (err) {
        console.error("❌ Error fetching places:", err);
      }
    };
    fetchPlaces();
  }, []);


  useEffect(() => {
  let alive = true;
  (async () => {
    try {
      const res = await axios.get("/auth/whoami");
      if (!alive) return;
      setCompanyName(String(res.data?.company_name || "").trim());
    } catch (e) {
      console.warn("whoami fetch failed:", e?.response?.status);
      setCompanyName("");
    }
  })();
  return () => { alive = false; };
}, []);


  useEffect(() => {
    const dateInputs = document.querySelectorAll('input[type="date"]');
    const timeInputs = document.querySelectorAll('input[type="time"]');

    const updateInputColor = (input) => {
      input.style.color = input.value ? "white" : "#aaa";
    };

    [...dateInputs, ...timeInputs].forEach((input) => {
      updateInputColor(input);
      input.addEventListener("input", () => updateInputColor(input));
    });

    return () => {
      [...dateInputs, ...timeInputs].forEach((input) => {
        input.removeEventListener("input", () => updateInputColor(input));
      });
    };
  }, []);

  useEffect(() => {
    deliveryDateRefs.current = deliveryPlaces.map((_, i) => deliveryDateRefs.current[i] || null);
  }, [deliveryPlaces]);

  useEffect(() => {
    const isValidId =
      existingTransportSessionId &&
      existingTransportSessionId !== "null";

    if (isValidId) {
      setCurrentLoad({
        transportsessionId: Number(existingTransportSessionId),
        id: "TBD",
      });

      setInitializingSession(false);
      return;
    }

    setCurrentLoad({
      transportsessionId: null,
      id: "TBD",
    });

    handleFetchNewTransportSession();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingTransportSessionId]);

  useEffect(() => {
    handleLengthChange({ target: { value: lengthInput } });
    handleHeightChange({ target: { value: heightInput } });
    handleWidthChange({ target: { value: widthInput } });
    handleWeightChange({ target: { value: weightInput } }); // 🆕
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialLoad, loadBearer]);

  useEffect(() => {
    const fetchExistingPoints = async () => {
      const sessionId = currentLoad?.transportsessionId;

      if (!sessionId || typeof sessionId !== "number") {
        console.warn("⛔ Skipping fetch: sessionId inválido");
        return;
      }

      try {
        console.log("📡 Request made to: /pickupdeliveries/" + sessionId);
        const response = await axios.get(`/pickupdeliveries/${sessionId}`);
        setExistingDateHourConflicts(response.data);
      } catch (err) {
        console.error("❌ Error fetching existing pickup/delivery points:", err);
      }
    };
    fetchExistingPoints();
  }, [currentLoad]);

  useEffect(() => {
    console.log("🎯 Conflict Check Triggered");
    console.log("➡️ existingDateHourConflicts:", existingDateHourConflicts);

    const allNewPoints = [...pickUpPlaces, ...deliveryPlaces];
    const conflictMessages = [];
    const conflictPairs = new Set();

    allNewPoints.forEach((newPoint) => {
      const match = existingDateHourConflicts.find(
        (existing) => existing.date === newPoint.date && existing.hour === newPoint.hour
      );

      if (match) {
        conflictMessages.push(
          t("errors.conflictWithExisting", {
            type: pointTypeLabel(match.type),
            location: match.location,
            date: match.date,
            hour: match.hour,
          })
        );
        conflictPairs.add(`${match.date}|${match.hour}`);
      }
    });

    setError(conflictMessages.join("\n"));
    setConflictDateHours(conflictPairs);
  }, [pickUpPlaces, deliveryPlaces, existingDateHourConflicts, t, i18n.language]);

useEffect(() => {
  let alive = true;

  (async () => {
    const [iRes, cRes] = await Promise.allSettled([
      axios.get("/industries"),
      axios.get("/carriers"),
    ]);

    if (!alive) return;

    const getClientName = (x) => String(x?.companyName || x?.name || "").trim();

    if (iRes.status === "fulfilled") {
      const inds = (iRes.value.data || []).sort((a, b) =>
        getClientName(a).localeCompare(getClientName(b), undefined, { sensitivity: "base" })
      );
      setIndustryClients(inds);
    } else {
      console.warn("industries fetch failed:", iRes.reason?.response?.status);
    }

    if (cRes.status === "fulfilled") {
      const cars = (cRes.value.data || []).sort((a, b) =>
        getClientName(a).localeCompare(getClientName(b), undefined, { sensitivity: "base" })
      );
      setCarrierClients(cars);
    } else {
      console.warn("carriers fetch failed:", cRes.reason?.response?.status, cRes.reason?.response?.data);
    }
  })();

  return () => {
    alive = false;
  };
}, []);



useEffect(() => {
  const cars = carrierClients || [];

  const carrierDisplayName = (c) => {
    const company =
      c?.name ||                 // ✅ your API returns `name`
      c?.companyName ||
      c?.company?.companyName ||
      c?.company?.name ||
      c?.businessName ||
      c?.legalName ||
      c?.tradeName ||
      "";

    const person = [c?.firstName, c?.lastName].filter(Boolean).join(" ").trim();
    return String(company || person || `Carrier #${c?.id || ""}`).trim();
  };

  const carrierSearchText = (c) => {
    const parts = [
      carrierDisplayName(c),
      c?.companyName,
      c?.company?.companyName,
      c?.vatNumber,
      c?.nif,
      c?.email,
      c?.phone,
    ];
    return parts.filter(Boolean).join(" ").toLowerCase();
  };

  const internalOpt =
    companyName && String(companyName).trim()
      ? {
          value: "__internal__",
          label: `${String(companyName).trim()} (internal)`,
          searchText: `${String(companyName).trim()} internal`.toLowerCase(),
          isInternal: true,
        }
      : null;

  setAvailableCarriers([
    ...(internalOpt ? [internalOpt] : []),
    ...cars.map((c) => ({
      value: String(c.id),
      label: carrierDisplayName(c),
      searchText: carrierSearchText(c),
      isInternal: false,
    })),
  ]);
}, [carrierClients, companyName]);

// ✅ NEW: auto-select company internal carrier by default (only if user hasn't chosen yet)
useEffect(() => {
  // don't override user choice
  if (transportCarrierId != null && String(transportCarrierId).trim() !== "") return;

  const internal = (availableCarriers || []).find(
    (c) => c?.isInternal || String(c?.value) === "__internal__"
  );

  if (internal) {
    setTransportCarrierId(String(internal.value));
    setCarrierError(false);
  }
}, [availableCarriers, transportCarrierId]);


useEffect(() => {
  const fetchTotalUsedSpace = async () => {
    if (existingTransportSessionId) {
      try {
        const response = await axios.get(`/loads?transportsessionId=${existingTransportSessionId}`);
        if (response.status === 200) {
          const sessionLoads = response.data;

          const usedLength = sessionLoads
            .filter((load) => !load.superImposable)
            .reduce((acc, load) => acc + parseFloat(load.length || 0), 0);

            const usedHeight = sessionLoads.reduce((acc, load) => acc + parseFloat(load.height || 0), 0);
            const usedWidth = sessionLoads.reduce((acc, load) => acc + parseFloat(load.width || 0), 0);

            setTotalUsedLength(usedLength);
            setTotalUsedHeight(usedHeight);
            setTotalUsedWidth(usedWidth);
            setAvailableHeight(Math.max(0, maxAllowedHeight - usedHeight));
            setAvailableWidth(Math.max(0, maxAllowedWidth - usedWidth));
          }
        } catch (error2) {
          console.error("Error fetching used space:", error2);
        }
      }
    };
    fetchTotalUsedSpace();
  }, [existingTransportSessionId, maxAllowedHeight, maxAllowedWidth]);

  const isValidDate = (dateString) => {
    if (!dateString) return false;
    const [year, month, day] = dateString.split("-").map(Number);
    if (!year || !month || !day) return false;
    const d = new Date(dateString);
    return d.getFullYear() === year && d.getMonth() + 1 === month && d.getDate() === day;
  };

  const validateDateOnBlur = (value, index, type) => {
    if (!value || value.length < 10) return;

    const isInvalidFormat = !isValidDate(value);

    if (isInvalidFormat) {
      const errorMessage = t("errors.invalidDate", {
        defaultValue: "Invalid date.",
      });

      setDateFieldErrors((prev) => ({
        ...prev,
        [`${type}-${index}`]: errorMessage,
      }));

      if (type === "pickup") {
        const updated = [...pickUpPlaces];
        updated[index].date = "";
        setPickUpPlaces(updated);
      } else {
        const updated = [...deliveryPlaces];
        updated[index].date = "";
        setDeliveryPlaces(updated);
      }
    } else {
      setDateFieldErrors((prev) => {
        const copy = { ...prev };
        delete copy[`${type}-${index}`];
        return copy;
      });
    }
  };

  const triggerDateErrorModal = (index, type) => {
    setTimeout(() => {
      setDateErrorModal({ visible: true, type, index });

      if (type === "pickup") {
        const updated = [...pickUpPlaces];
        updated[index].date = "";
        setPickUpPlaces(updated);
      } else if (type === "delivery") {
        const updated = [...deliveryPlaces];
        updated[index].date = "";
        setDeliveryPlaces(updated);
      }

      setAlreadySubmitted(false);
      setLoading(false);
    }, 100);
  };

  const hasInvalidDates = () => {
    let invalidFound = false;

    pickUpPlaces.forEach((place, index) => {
      if (place.date && !isValidDate(place.date)) {
        triggerDateErrorModal(index, "pickup");
        invalidFound = true;
      }
    });

    deliveryPlaces.forEach((place, index) => {
      if (place.date && !isValidDate(place.date)) {
        triggerDateErrorModal(index, "delivery");
        invalidFound = true;
      }
    });

    return invalidFound;
  };

  const handleFetchNewTransportSession = async () => {
    setInitializingSession(true);
    setMessage("");

    try {
      const response = await axios.post("/loads/pre-create", {});

      if (response.status === 200) {
        setCurrentLoad({
          transportsessionId:
            response.data?.load?.transportsessionId ?? null,
          id:
            response.data?.load?.id ?? "TBD",
        });

        return;
      }

      setMessage(t("errors.failedToInitializeLoad"));
    } catch (error2) {
      console.error("Error initializing Load (preview):", error2);

      setMessage(t("errors.serverErrorTryAgain"));
    } finally {
      setInitializingSession(false);
    }
  };

  const handleLengthChange = (event) => {
    let value = event.target.value;

    if (/^\d{0,2}(\.\d{0,2})?$/.test(value) || value === "") {
      setLengthInput(value);
    }

    const inputLength = parseFloat(value);

    if (!value) {
      if (!lengthError) setLengthError("");
      return;
    }

    if (!value || Number.isNaN(inputLength)) {
      setLengthError(t("errors.enterValidNumber"));
      return;
    }

    if (specialLoad) {
      // ✅ allow normal length too; only validate "special" if it exceeds normal max
      const normalMax = Math.max(0, parseFloat((maxAllowedLength - totalUsedLength).toFixed(2))); // usually 13.60
      const specialMin = 13.61;
      const specialMax = 20;

      if (inputLength <= normalMax) {
        setLengthError("");
      } else if (inputLength >= specialMin && inputLength <= specialMax) {
        setLengthError("");
      } else {
        setLengthError(t("errors.specialLoadLengthRange"));
      }
    } else {
      const availableSpace = Math.max(0, parseFloat((maxAllowedLength - totalUsedLength).toFixed(2)));
      if (inputLength > availableSpace) {
        setLengthError(t("errors.exceedsAvailableSpace", { max: availableSpace.toFixed(2) }));
      } else {
        setLengthError("");
      }
    }
  };

  const handleHeightChange = (event) => {
    let value = event.target.value;

    if (/^\d{0,1}(\.\d{0,2})?$/.test(value) || value === "") {
      setHeightInput(value);
    }

    const inputHeight = parseFloat(value);

    if (!value) {
      if (!heightError) setHeightError("");
      return;
    }

    if (!value || Number.isNaN(inputHeight)) {
      setHeightError(t("errors.enterValidNumber"));
      return;
    }

    if (specialLoad) {
      const normalMax = 2.7;
      const specialMin = 2.71;
      const specialMax = 5;

      if (inputHeight <= normalMax) {
        setHeightError("");
      } else if (inputHeight >= specialMin && inputHeight <= specialMax) {
        setHeightError("");
      } else {
        setHeightError(t("errors.specialLoadHeightRange"));
      }
    } else {
      if (inputHeight > 2.7) {
        setHeightError(t("errors.loadHeightMax", { max: "2.70" }));
      } else {
        setHeightError("");
      }
    }
  };

  const handleWidthChange = (event) => {
    let value = event.target.value;

    if (/^\d{0,1}(\.\d{0,2})?$/.test(value) || value === "") {
      setWidthInput(value);
    }

    const inputWidth = parseFloat(value);

    if (!value) {
      if (!widthError) setWidthError("");
      return;
    }

    if (!value || Number.isNaN(inputWidth)) {
      setWidthError(t("errors.enterValidNumber"));
      return;
    }

    if (specialLoad) {
      const normalMax = 2.5;
      const specialMin = 2.51;
      const specialMax = 4;

      if (inputWidth <= normalMax) {
        setWidthError("");
      } else if (inputWidth >= specialMin && inputWidth <= specialMax) {
        setWidthError("");
      } else {
        setWidthError(t("errors.specialLoadWidthRange"));
      }
    } else {
      if (inputWidth > 2.5) {
        setWidthError(t("errors.loadWidthMax", { max: "2.50" }));
      } else {
        setWidthError("");
      }
    }
  };

  const handleWeightChange = (event) => {
    let value = event.target.value;

    // permite até 2 casas decimais (ajusta se quiseres)
    if (/^\d{0,7}(\.\d{0,2})?$/.test(value) || value === "") {
      setWeightInput(value);
    }

    const inputWeight = parseFloat(value);

    if (!value) {
      if (!weightError) setWeightError("");
      return;
    }

    if (Number.isNaN(inputWeight)) {
      setWeightError(t("errors.enterValidNumber"));
      return;
    }

    if (inputWeight <= 0) {
      setWeightError(
        t("errors.weightRequired", { defaultValue: "❌ Weight is required." })
      );
      return;
    }

    setWeightError("");
  };

  const handlePickUpPlaceChange = (event, index, field) => {
    const value = event.target.value;
    const updated = [...pickUpPlaces];

    updated[index] = {...updated[index], [field]: value};
    if (["address", "postalCode", "city", "country", "coordinates", "gMapsLink"].includes(field)
        && updated[index].aiGeocode?.proposedLocation) {
      if (updated[index].aiGeocode.proposedLocation.reviewStatus ===
          "CONFIRMED_BY_DISPATCHER") {
        if (field !== "coordinates") updated[index].coordinates = "";
        if (field !== "gMapsLink") updated[index].gMapsLink = "";
      }
      updated[index].aiGeocode = {
        ...updated[index].aiGeocode,
        proposedLocation: null
      };
    }

    if (["length", "width", "height"].includes(field)) {
      const dimensionError = getPointDimensionError(field, value);

      updated[index][`${field}Error`] = dimensionError;

      setInvalidFields((prev) => {
        const copy = { ...prev };

        if (dimensionError) {
          copy[`pickup-${field}-${index}`] = true;
        } else {
          delete copy[`pickup-${field}-${index}`];
        }

        return copy;
      });
    } else {
      setInvalidFields((prev) => {
        const copy = { ...prev };
        delete copy[`pickup-${field}-${index}`];
        return copy;
      });
    }

    setPickUpPlaces(updated);

    const required = [
      "location",
      "date",
      "hour",
      "address",
      "postalCode",
      "city",
      "country",
      "gMapsLink",
      "coordinates",
      "contactPhone",
    ];

    const thisPlace = updated[index];
    const allFilled = required.every((f) => (thisPlace[f] ?? "").toString().trim() !== "");
    if (allFilled) setPickUpPlaceError("");
  };

  const addPickUpPlace = () => {
    if (pickUpPlaces.length < MAX_POINTS) {
      setPickUpPlaces([
        ...pickUpPlaces,
        {
          _uiId: createStopUiId(),
          location: "",
          date: "",
          hour: "",
          scheduleType: "FIXED",
          hourEnd: null,

          // 🆕 point-level dimensions
          length: "",
          width: "",
          height: "",

          placeId: null,
          address: "",
          postalCode: "",
          city: "",
          country: "",
          gMapsLink: "",
          coordinates: "",
          contactPhone: "",
          workingHours: null,
          isNewPlace: false, // 🆕 typed manually in CreatableSelect
          loadingAccess: "SIDE",
          handlingEquipment: "FORKLIFT",
          handlingEquipmentOther: "",
          reference: "", // 🆕
          showMoreFields: true,
          locationError: false,
          dateError: false,
          hourError: false,
          addressError: false,
          postalCodeError: false,
          cityError: false,
          countryError: false,
          gMapsLinkError: false,
          coordinatesError: false,
          contactPhoneError: false,
        },
      ]);
      setActivePickUpIndex(pickUpPlaces.length);
    }
  };

  const removePickUpPlace = (index) => {
    const updatedPlaces = pickUpPlaces.filter((_, i) => i !== index);
    setPickUpPlaces(updatedPlaces);
    setActivePickUpIndex(Math.max(0, index - 1));
  };

  const handleDeliveryPlaceChange = (event, index, field) => {
    const value = event.target.value;
    const updated = [...deliveryPlaces];

    updated[index] = {...updated[index], [field]: value};
    if (["location", "address", "postalCode", "city", "country", "coordinates", "gMapsLink"].includes(field)
        && updated[index].aiGeocode?.proposedLocation) {
      if (updated[index].aiGeocode.proposedLocation.reviewStatus ===
          "CONFIRMED_BY_DISPATCHER") {
        if (field !== "coordinates") updated[index].coordinates = "";
        if (field !== "gMapsLink") updated[index].gMapsLink = "";
      }
      updated[index].aiGeocode = {
        ...updated[index].aiGeocode,
        proposedLocation: null
      };
    }

    if (["length", "width", "height"].includes(field)) {
      const dimensionError = getPointDimensionError(field, value);

      updated[index][`${field}Error`] = dimensionError;

      setInvalidFields((prev) => {
        const copy = { ...prev };

        if (dimensionError) {
          copy[`delivery-${field}-${index}`] = true;
        } else {
          delete copy[`delivery-${field}-${index}`];
        }

        return copy;
      });
    } else {
      setInvalidFields((prev) => {
        const copy = { ...prev };
        delete copy[`delivery-${field}-${index}`];
        return copy;
      });
    }

    setDeliveryPlaces(updated);

    const required = [
      "location",
      "date",
      "hour",
      "address",
      "postalCode",
      "city",
      "country",
      "gMapsLink",
      "coordinates",
      "contactPhone",
    ];

    const thisPlace = updated[index];
    const allFilled = required.every((f) => (thisPlace[f] ?? "").toString().trim() !== "");
    if (allFilled) setDeliveryPlaceError("");
  };

  const addDeliveryPlace = () => {
    if (deliveryPlaces.length < MAX_POINTS) {
      setDeliveryPlaces([
        ...deliveryPlaces,
        {
          _uiId: createStopUiId(),
          location: "",
          date: "",
          hour: "",
          scheduleType: "FIXED",
          hourEnd: null,

          // 🆕 point-level dimensions
          length: "",
          width: "",
          height: "",

          placeId: null,
          address: "",
          postalCode: "",
          city: "",
          country: "",
          gMapsLink: "",
          coordinates: "",
          contactPhone: "",
          workingHours: null,
          isNewPlace: false, // 🆕 typed manually in CreatableSelect
          loadingAccess: "SIDE",
          handlingEquipment: "FORKLIFT",
          handlingEquipmentOther: "",
          reference: "", // 🆕
          showMoreFields: true,
          hourError: false,
        },
      ]);
    }
  };

  const removeDeliveryPlace = (index) => {
    const updatedPlaces = deliveryPlaces.filter((_, i) => i !== index);
    setDeliveryPlaces(updatedPlaces);
  };



  const NORMAL_MAX = {
    length: Number(maxAllowedLength),  // ex.: 13.6
    height: Number(maxAllowedHeight),  // 2.7
    width:  Number(maxAllowedWidth),   // 2.5
  };

  const SPECIAL_RANGE = {
    length: { min: 13.61, max: 20 },
    height: { min: 2.71,  max: 5  },
    width:  { min: 2.51,  max: 4  },
  };

  const inRange = (n, a, b) => Number.isFinite(n) && n >= a && n <= b;

  const isSpecialLength = (n) => inRange(n, SPECIAL_RANGE.length.min, SPECIAL_RANGE.length.max);
  const isSpecialHeight = (n) => inRange(n, SPECIAL_RANGE.height.min, SPECIAL_RANGE.height.max);
  const isSpecialWidth  = (n) => inRange(n, SPECIAL_RANGE.width.min,  SPECIAL_RANGE.width.max);

  const isNormalLength = (n) => Number.isFinite(n) && n > 0 && n <= NORMAL_MAX.length;
  const isNormalHeight = (n) => Number.isFinite(n) && n > 0 && n <= NORMAL_MAX.height;
  const isNormalWidth  = (n) => Number.isFinite(n) && n > 0 && n <= NORMAL_MAX.width;

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";
    if (!Number.isFinite(n) || n <= 0) return t("errors.enterValidNumber");

    if (field === "length") {
      const availableSpace = Math.max(
        0,
        parseFloat((maxAllowedLength - totalUsedLength).toFixed(2))
      );

      if (specialLoad) {
        if (isNormalLength(n) || isSpecialLength(n)) return "";
        return t("errors.specialLoadLengthRange");
      }

      if (n > availableSpace) {
        return t("errors.exceedsAvailableSpace", {
          max: availableSpace.toFixed(2),
        });
      }

      return "";
    }

    if (field === "width") {
      if (specialLoad) {
        if (isNormalWidth(n) || isSpecialWidth(n)) return "";
        return t("errors.specialLoadWidthRange");
      }

      if (n > maxAllowedWidth) {
        return t("errors.loadWidthAtMost", {
          max: maxAllowedWidth.toFixed(2),
        });
      }

      return "";
    }

    if (field === "height") {
      if (specialLoad) {
        if (isNormalHeight(n) || isSpecialHeight(n)) return "";
        return t("errors.specialLoadHeightRange");
      }

      if (n > maxAllowedHeight) {
        return t("errors.loadHeightAtMost", {
          max: maxAllowedHeight.toFixed(2),
        });
      }

      return "";
    }

    return "";
  };

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const length = Number(point.length || 0);
        const width = Number(point.width || 0);
        const height = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(length) ? length : 0),
          width: Math.max(acc.width, Number.isFinite(width) ? width : 0),
          height: Math.max(acc.height, Number.isFinite(height) ? height : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickUpPlaces);
  }, [pickUpPlaces]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPlaces);
  }, [deliveryPlaces]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    const lengthMatches =
      Math.round(pickupLength * 100) === Math.round(deliveryLength * 100);

    return {
      length: lengthMatches ? pickupLength : pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches,
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  // 🆕 Normalize place identity to avoid asking/saving duplicates
  const normalizePlaceIdentity = (value) => {
    return String(value ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  const getPlaceIdentityKey = (pointOrPlace) => {
    return [
      normalizePlaceIdentity(pointOrPlace.placeName || pointOrPlace.location),
      normalizePlaceIdentity(pointOrPlace.address),
      normalizePlaceIdentity(pointOrPlace.postalCode),
      normalizePlaceIdentity(pointOrPlace.city),
      normalizePlaceIdentity(pointOrPlace.country),
    ].join("|");
  };

  const alreadyExistsInPlaces = (point) => {
    const pointKey = getPlaceIdentityKey(point);

    return (allPlaces || []).some((place) => {
      return getPlaceIdentityKey(place) === pointKey;
    });
  };

  const normalizePhoneDigits = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    return digits.length > 0 ? digits : null;
  };

  const buildPlacePayloadFromPoint = (point) => ({
    placeName: String(point.location || "").trim(),
    category: "OTHER", // ✅ must match backend allowed categories
    address: String(point.address || "").trim(),
    postalCode: String(point.postalCode || "").trim(),
    city: String(point.city || "").trim(),
    country: String(point.country || "").trim(),
    gMapsLink: String(point.gMapsLink || "").trim() || null,
    coordinates: String(point.coordinates || "").trim() || null,
    contactPhone: normalizePhoneDigits(point.contactPhone),
    workingHours: point.workingHours ?? null,
  });

  const collectNewPlaceCandidates = () => {
    const allPoints = [
      ...pickUpPlaces.map((point, index) => ({
        ...point,
        pointType: "PICKUP",
        pointIndex: index,
      })),
      ...deliveryPlaces.map((point, index) => ({
        ...point,
        pointType: "DELIVERY",
        pointIndex: index,
      })),
    ];

    const requiredPlaceFields = [
      "location",
      "address",
      "postalCode",
      "city",
      "country",
      "gMapsLink",
      "coordinates",
      "contactPhone",
    ];

    const candidates = [];
    const seen = new Set();

    allPoints.forEach((point) => {
      const isTypedNewPlace =
        point.isNewPlace === true &&
        !point.placeId &&
        String(point.location || "").trim() !== "";

      if (!isTypedNewPlace) return;

      const hasAllPlaceFields = requiredPlaceFields.every((field) => {
        return String(point[field] ?? "").trim() !== "";
      });

      if (!hasAllPlaceFields) return;
      if (alreadyExistsInPlaces(point)) return;

      const key = getPlaceIdentityKey(point);
      if (seen.has(key)) return;

      seen.add(key);

      candidates.push({
        key,
        pointType: point.pointType,
        pointIndex: point.pointIndex,
        label: point.location,
        payload: buildPlacePayloadFromPoint(point),
      });
    });

    return candidates;
  };

  const submitLoadPayload = async (payload) => {
    try {
      console.log("📤 Payload being sent to backend:", payload);
      const loadResponse = await axios.post("/loads/create", payload);

      if (loadResponse.status !== 201) {
        setMessage(t("errors.failedToCreateLoad"));
        return;
      }

      const newLoad = loadResponse.data.load;
      console.log("✅ Load Created Successfully:", newLoad);
      setSuccessPopupOpen(true);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      console.error("🚨 Error creating Load (axios):", {
        status,
        url: err?.config?.url,
        method: err?.config?.method,
        data,
        message: err?.message,
      });

      try {
        console.error("🚨 Error data pretty:", JSON.stringify(data, null, 2));
      } catch {}

      setMessage(
        data?.message || data?.error || t("errors.serverErrorTryAgain")
      );
    } finally {
      setLoading(false);
      setAlreadySubmitted(false);
    }
  };

  const handleCreateWithoutSavingNewPlaces = async () => {
    const payload = saveNewPlacesModal.payload;

    setSaveNewPlacesModal({
      visible: false,
      candidates: [],
      payload: null,
      saving: false,
    });

    if (!payload) {
      setLoading(false);
      setAlreadySubmitted(false);
      return;
    }

    setLoading(true);
    await submitLoadPayload(payload);
  };

  const handleSaveNewPlacesAndCreateLoad = async () => {
    const { candidates, payload } = saveNewPlacesModal;

    if (!payload) {
      setSaveNewPlacesModal({
        visible: false,
        candidates: [],
        payload: null,
        saving: false,
      });
      setLoading(false);
      setAlreadySubmitted(false);
      return;
    }

    setSaveNewPlacesModal((prev) => ({
      ...prev,
      saving: true,
    }));

    try {
      const createdPlaces = [];

      for (const candidate of candidates) {
        try {
          const response = await axios.post("/places", candidate.payload);
          if (response?.data) {
            createdPlaces.push(response.data);
          }
        } catch (err) {
          // ✅ If another user already created it, do not block load creation
          if (err?.response?.status === 409) {
            console.warn("⚠️ Place already exists, skipping:", candidate.payload.placeName);
            continue;
          }

          throw err;
        }
      }

      if (createdPlaces.length > 0) {
        setAllPlaces((prev) => {
          const merged = [...(prev || []), ...createdPlaces];
          const map = new Map();

          merged.forEach((place) => {
            map.set(getPlaceIdentityKey(place), place);
          });

          return Array.from(map.values());
        });
      }

      setSaveNewPlacesModal({
        visible: false,
        candidates: [],
        payload: null,
        saving: false,
      });

      setLoading(true);
      await submitLoadPayload(payload);
    } catch (err) {
      console.error("❌ Error saving new places:", err);

      setMessage(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          t("errors.serverErrorTryAgain")
      );

      setSaveNewPlacesModal((prev) => ({
        ...prev,
        saving: false,
      }));

      setLoading(false);
      setAlreadySubmitted(false);
    }
  };

  useEffect(() => {
    const nextLength = pointDimensionSummary.length > 0
      ? pointDimensionSummary.length.toFixed(2)
      : "";

    const nextWidth = pointDimensionSummary.width > 0
      ? pointDimensionSummary.width.toFixed(2)
      : "";

    const nextHeight = pointDimensionSummary.height > 0
      ? pointDimensionSummary.height.toFixed(2)
      : "";

    setLengthInput(nextLength);
    setWidthInput(nextWidth);
    setHeightInput(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  const handleCreateLoad = async () => {
    if (["QUEUED", "PROCESSING"].includes(
      String(aiImportJob?.status || "").toUpperCase()
    )) {
      setMessage(
        "You can edit the extracted fields while locations are being verified. " +
        "Please wait for location analysis to finish before creating the load."
      );
      return;
    }
    if (alreadySubmitted) return;
    setAlreadySubmitted(true);
    setLoading(true);
    setInvalidFields({});

    const invalidDatesFound = hasInvalidDates();

    setError("");
    let hasError = false;

    const inputLength = parseFloat(lengthInput);
    const inputHeight = parseFloat(heightInput);
    const inputWidth = parseFloat(widthInput);
    const inputWeight = parseFloat(weightInput); // 🆕
    const availableSpace = Math.max(0, parseFloat((maxAllowedLength - totalUsedLength).toFixed(2)));

    // ✅ SpecialLoad must have at least ONE special dimension
    if (
      specialLoad &&
      [inputLength, inputHeight, inputWidth].some(Number.isFinite)
    ) {
      const isLengthSpecial = Number.isFinite(inputLength) && inputLength > availableSpace; // > 13.60 normally
      const isHeightSpecial = Number.isFinite(inputHeight) && inputHeight > maxAllowedHeight; // > 2.70
      const isWidthSpecial  = Number.isFinite(inputWidth)  && inputWidth > maxAllowedWidth;  // > 2.50

      if (!isLengthSpecial && !isHeightSpecial && !isWidthSpecial) {
        setError(
          t(
            "errors.specialLoadNeedsOneSpecialDimension",
            { defaultValue: "⚠️ This is marked as Special Load, but no dimension is special. Please increase at least one of: length, width, or height." }
          )
        );
        hasError = true;
      }
    }

    // ✅ Reset do aviso global do SpecialLoad (no início da validação do submit)
    setSpecialDimError("");

    // ✅ Total dimensions are auto-calculated from points.
    // Do NOT paint the Merchandise/summary fields red anymore.
    setLengthError("");
    setHeightError("");
    setWidthError("");

    // ✅ NOVO: Regra do SpecialLoad -> pelo menos 1 dimensão tem de ser "especial"
    if (
      specialLoad &&
      [inputLength, inputHeight, inputWidth].some(Number.isFinite)
    ) {
      const anySpecial =
        isSpecialLength(inputLength) ||
        isSpecialHeight(inputHeight) ||
        isSpecialWidth(inputWidth);

      if (!anySpecial) {
        setSpecialDimError(t("errors.specialLoadNeedsOneSpecialDimension"));
        hasError = true;
      } else {
        setSpecialDimError("");
      }
    }

    // 🆕 Weight (optional, but validated when provided)
    if (
      String(weightInput ?? "").trim() !== "" &&
      (Number.isNaN(inputWeight) || inputWeight <= 0)
    ) {
      setWeightError(
        t("errors.enterValidNumber", {
          defaultValue: "❌ Please enter a valid number.",
        })
      );
      hasError = true;
    } else {
      setWeightError("");
    }

    // 🆕 Merchandise (required)
    if (!String(merchandise || "").trim()) {
      setMerchandiseError(
        t("errors.merchandiseRequired", { defaultValue: "❌ Merchandise is required." })
      );
      hasError = true;
    } else {
      setMerchandiseError("");
    }

    // 🆕 Trailer Type (optional)
    setTrailerTypeError("");

    // 🆕 Trailer Type Other (required only when OTHER)
    if (trailerType === "OTHER") {
      if (!String(trailerTypeOther || "").trim()) {
        setTrailerTypeOtherError(
          t("errors.trailerTypeOtherRequired", { defaultValue: "❌ Please specify the trailer type." })
        );
        hasError = true;
      } else {
        setTrailerTypeOtherError("");
      }
    } else {
      // if not OTHER, always clear
      setTrailerTypeOtherError("");
    }

    // Pick-Up validation
    const newInvalids = {};
    const updatedPickup = [...pickUpPlaces];

    updatedPickup.forEach((place, i) => {
      let hasErrorInThis = false;

      if (!String(place.location || "").trim()) {
        newInvalids[`pickup-location-${i}`] = true;
        hasErrorInThis = true;
      }

      const pointLengthError =
        String(place.length ?? "").trim() !== ""
          ? getPointDimensionError("length", place.length)
          : "";

      const pointWidthError =
        String(place.width ?? "").trim() !== ""
          ? getPointDimensionError("width", place.width)
          : "";

      const pointHeightError =
        String(place.height ?? "").trim() !== ""
          ? getPointDimensionError("height", place.height)
          : "";

      updatedPickup[i].lengthError = pointLengthError;
      updatedPickup[i].widthError = pointWidthError;
      updatedPickup[i].heightError = pointHeightError;

      if (pointLengthError) {
        newInvalids[`pickup-length-${i}`] = true;
        hasErrorInThis = true;
      }

      if (pointWidthError) {
        newInvalids[`pickup-width-${i}`] = true;
        hasErrorInThis = true;
      }

      if (pointHeightError) {
        newInvalids[`pickup-height-${i}`] = true;
        hasErrorInThis = true;
      }

      if (
        String(place.handlingEquipment || "").toUpperCase() === "OTHER" &&
        !String(place.handlingEquipmentOther || "").trim()
      ) {
        newInvalids[`pickup-handlingEquipmentOther-${i}`] = true;
        hasErrorInThis = true;
      }

      if (hasErrorInThis) {
        updatedPickup[i].showMoreFields = true;
      }
    });

    const hasPickupLocationError = updatedPickup.some(
      (place) => !String(place.location || "").trim()
    );

    if (Object.keys(newInvalids).length > 0) {
      hasError = true;
      setInvalidFields((prev) => ({ ...prev, ...newInvalids }));
      setPickUpPlaceError(
        hasPickupLocationError ? t("errors.locationRequired") : ""
      );
    } else {
      setPickUpPlaceError("");
    }

    setPickUpPlaces(updatedPickup);

    // Delivery validation
    const deliveryInvalids = {};
    const updatedDelivery = [...deliveryPlaces];

    updatedDelivery.forEach((place, i) => {
      let hasErrorInThis = false;

      if (!String(place.location || "").trim()) {
        deliveryInvalids[`delivery-location-${i}`] = true;
        hasErrorInThis = true;
      }

      const pointLengthError =
        String(place.length ?? "").trim() !== ""
          ? getPointDimensionError("length", place.length)
          : "";

      const pointWidthError =
        String(place.width ?? "").trim() !== ""
          ? getPointDimensionError("width", place.width)
          : "";

      const pointHeightError =
        String(place.height ?? "").trim() !== ""
          ? getPointDimensionError("height", place.height)
          : "";

      updatedDelivery[i].lengthError = pointLengthError;
      updatedDelivery[i].widthError = pointWidthError;
      updatedDelivery[i].heightError = pointHeightError;

      if (pointLengthError) {
        deliveryInvalids[`delivery-length-${i}`] = true;
        hasErrorInThis = true;
      }

      if (pointWidthError) {
        deliveryInvalids[`delivery-width-${i}`] = true;
        hasErrorInThis = true;
      }

      if (pointHeightError) {
        deliveryInvalids[`delivery-height-${i}`] = true;
        hasErrorInThis = true;
      }

      if (
        String(place.handlingEquipment || "").toUpperCase() === "OTHER" &&
        !String(place.handlingEquipmentOther || "").trim()
      ) {
        deliveryInvalids[`delivery-handlingEquipmentOther-${i}`] = true;
        hasErrorInThis = true;
      }

      if (hasErrorInThis) {
        updatedDelivery[i].showMoreFields = true;
      }
    });

    const hasDeliveryLocationError = updatedDelivery.some(
      (place) => !String(place.location || "").trim()
    );

    if (Object.keys(deliveryInvalids).length > 0) {
      hasError = true;
      setInvalidFields((prev) => ({ ...prev, ...deliveryInvalids }));
      setDeliveryPlaceError(
        hasDeliveryLocationError ? t("errors.locationRequired") : ""
      );
    } else {
      setDeliveryPlaceError("");
    }

    setDeliveryPlaces(updatedDelivery);

    const toCents = (value) => Math.round(Number(value || 0) * 100);

    const pickupLengthTotal = pickUpPlaces.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const deliveryLengthTotal = deliveryPlaces.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const allPickupLengthsProvided =
      pickUpPlaces.length > 0 &&
      pickUpPlaces.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    if (
      Number.isFinite(inputLength) &&
      inputLength > 0 &&
      allPickupLengthsProvided &&
      toCents(pickupLengthTotal) !== toCents(inputLength)
    ) {
      hasError = true;

      setPickUpPlaceError(
        t("errors.pickupLengthTotalMustMatchLoad", {
          defaultValue: "❌ Total Pick-Up length must equal the Load length.",
        })
      );

      const lengthInvalids = {};
      pickUpPlaces.forEach((_, i) => {
        lengthInvalids[`pickup-length-${i}`] = true;
      });

      setInvalidFields((prev) => ({
        ...prev,
        ...lengthInvalids,
      }));
    }

    const allDeliveryLengthsProvided =
      deliveryPlaces.length > 0 &&
      deliveryPlaces.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    if (
      Number.isFinite(inputLength) &&
      inputLength > 0 &&
      allDeliveryLengthsProvided &&
      toCents(deliveryLengthTotal) !== toCents(inputLength)
    ) {
      hasError = true;

      setDeliveryPlaceError(
        t("errors.deliveryLengthTotalMustMatchLoad", {
          defaultValue: "❌ Total Delivery length must equal the Load length.",
        })
      );

      const lengthInvalids = {};
      deliveryPlaces.forEach((_, i) => {
        lengthInvalids[`delivery-length-${i}`] = true;
      });

      setInvalidFields((prev) => ({
        ...prev,
        ...lengthInvalids,
      }));
    }

    const normalizedPrice = Number(normalizeMoney(priceInput));

    if (!String(priceInput || "").trim()) {
      setPriceError(
        t("errors.priceRequired", {
          defaultValue: "❌ Price is required.",
        })
      );
      hasError = true;
    } else if (!Number.isFinite(normalizedPrice) || normalizedPrice < 0) {
      setPriceError(
        t("errors.priceMustBePositive", {
          defaultValue: "❌ Price must be a valid non-negative number.",
        })
      );
      hasError = true;
    } else {
      setPriceError("");
    }

    if (!clientType) {
      setClientTypeError(true);
      hasError = true;
    } else {
      setClientTypeError(false);
    }

    if (!selectedClient?.value) {
      setSelectedClientError(true);
      hasError = true;
    } else {
      setSelectedClientError(false);
    }

    // Pickup and delivery may occur in chronological order,
    // regardless of the operation type.

    // Transport carrier is optional
    setCarrierError(false);

    if (hasError || invalidDatesFound) {
      setLoading(false);
      setAlreadySubmitted(false);
      return;
    }

    // Check time conflicts
    try {
      const sessionId = currentLoad?.transportsessionId;
      if (sessionId && typeof sessionId === "number") {
        const response = await axios.get(`/pickupdeliveries/${sessionId}`);
        const existingPoints = response.data;

        const conflictErrors = [];
        const allNewPoints = [
          ...pickUpPlaces.map((p) => ({ ...p, type: "PICKUP" })),
          ...deliveryPlaces.map((d) => ({ ...d, type: "DELIVERY" })),
        ];

        for (const newPoint of allNewPoints) {
          if (!newPoint.date || !newPoint.hour) continue;

          const conflict = existingPoints.find(
            (existing) => existing.date === newPoint.date && existing.hour === newPoint.hour
          );
          if (conflict) {
            conflictErrors.push(
              t("errors.conflictWithExisting", {
                type: pointTypeLabel(conflict.type),
                location: conflict.location,
                date: conflict.date,
                hour: conflict.hour,
              })
            );
          }
        }

        if (conflictErrors.length > 0) {
          setError(conflictErrors.join("\n"));
          setLoading(false);
          setAlreadySubmitted(false);
          return;
        }
      }
    } catch (err) {
      console.error("❌ Error verifying pickup/delivery conflicts:", err);
      setError(t("errors.couldNotVerifyTimes"));
      setLoading(false);
      setAlreadySubmitted(false);
      return;
    }

    // Send to backend
    try {
      const firstPickup = pickUpPlaces.find((p) => String(p.location || "").trim() !== "") || {};
      const lastDelivery =
        [...deliveryPlaces].reverse().find((p) => String(p.location || "").trim() !== "") || {};

      const cleanNotes4driver =
        typeof notes4driver === "string" ? notes4driver.trim() || null : null;

      // ✅ NEW: define cleanNotes4subcontracted BEFORE payload
      const cleanNotes4subcontracted =
        showNotes4subcontracted && typeof notes4subcontracted === "string"
          ? (notes4subcontracted.trim() || null)
          : null;

      // ✅ NEW: clean price4subcontracted (optional)
      const cleanPrice4subcontracted =
        showNotes4subcontracted && price4subcontractedInput !== ""
          ? Number(price4subcontractedInput)
          : null;


      const selectedTransportOpt = availableCarriers.find(
        (c) => String(c.value) === String(transportCarrierId)
      );

      const isInternalCarrier = selectedTransportOpt?.value === "__internal__";


      // if not internal, try to send a numeric id; otherwise null
      const carrierIdPayload =
        isInternalCarrier ? null : (transportCarrierId ? Number(transportCarrierId) : null);

      const carrierNamePayload = isInternalCarrier
        ? (companyName || null)
        : (selectedTransportOpt?.label || null);


      const payload = {
        transportsessionId: currentLoad?.transportsessionId || null,
        length: Number.isFinite(inputLength) ? inputLength : null,
        height: Number.isFinite(inputHeight) ? inputHeight : null,
        width: Number.isFinite(inputWidth) ? inputWidth : null,
        weight: Number.isFinite(inputWeight) ? inputWeight : null,
        price: normalizedPrice,
        price4subcontracted: cleanPrice4subcontracted, // ✅ NEW
        priceCurrency,
        loadBearer,
        superImposable: false,
        associatedLoadBearerId: null,
        industryClientId: clientType === "industry" ? selectedClient?.value : null,
        carrierClientId: clientType === "carrier" ? selectedClient?.value : null,
        industryClientName: clientType === "industry" ? selectedClient?.label || null : null,
        carrierClientName: clientType === "carrier" ? selectedClient?.label || null : null,
        clientType: clientType || null,
        notes4driver: cleanNotes4driver,
        notes4subcontracted: cleanNotes4subcontracted,
        clientContactEmails: selectedClientContactEmails.map((opt) => opt.value),
        subcontractedEmails: selectedSubcontractedEmails.map((opt) => opt.value),
        carrierId: carrierIdPayload,
        carrierName: carrierNamePayload,
        merchandise: String(merchandise || "").trim() || null,
        trailerType: trailerType || null,
        trailerTypeOther: trailerType === "OTHER"
          ? (String(trailerTypeOther || "").trim() || null)
          : null,
        loadingAccess: firstPickup?.loadingAccess || null, // ✅ keep for compatibility (derived from first pickup)

        pickupLocations: pickUpPlaces
          .filter((p) => String(p.location || "").trim() !== "")
          .map((p) => ({
            location: p.location,
            date: p.date || null,
            hour: p.hour || null,
            scheduleType: p.scheduleType === "WINDOW" ? "WINDOW" : "FIXED",
            hourEnd: p.scheduleType === "WINDOW" ? (p.hourEnd || null) : null,

            // 🆕 point-level dimensions
            length: p.length === "" ? null : Number(p.length),
            width: p.width === "" ? null : Number(p.width),
            height: p.height === "" ? null : Number(p.height),

            placeId: p.placeId || null,
            address: p.address || "",
            postalCode: p.postalCode || "",
            city: p.city || "",
            country: p.country || "",
            gMapsLink: p.gMapsLink || "",
            coordinates: p.coordinates || "",
            contactPhone: p.contactPhone || "",
            reference: String(p.reference || "").trim() || null, // 🆕
            workingHours: p.workingHours ?? null,
            loadingAccess: p.loadingAccess || null, // ✅ NEW
            handlingEquipment: p.handlingEquipment || null, // 🆕 NEW
            handlingEquipmentOther: p.handlingEquipment === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
          })),

        deliveryLocations: deliveryPlaces
          .filter((d) => String(d.location || "").trim() !== "")
          .map((d) => ({
            location: d.location,
            date: d.date || null,
            hour: d.hour || null,
            scheduleType: d.scheduleType === "WINDOW" ? "WINDOW" : "FIXED",
            hourEnd: d.scheduleType === "WINDOW" ? (d.hourEnd || null) : null,

            // 🆕 point-level dimensions
            length: d.length === "" ? null : Number(d.length),
            width: d.width === "" ? null : Number(d.width),
            height: d.height === "" ? null : Number(d.height),

            placeId: d.placeId || null,
            address: d.address || "",
            postalCode: d.postalCode || "",
            city: d.city || "",
            country: d.country || "",
            gMapsLink: d.gMapsLink || "",
            coordinates: d.coordinates || "",
            contactPhone: d.contactPhone || "",
            reference: String(d.reference || "").trim() || null, // 🆕
            workingHours: d.workingHours ?? null,
            loadingAccess: d.loadingAccess || null, // ✅ NEW
            handlingEquipment: d.handlingEquipment || null, // 🆕 NEW
            handlingEquipmentOther: d.handlingEquipment === "OTHER"
              ? (String(d.handlingEquipmentOther || "").trim() || null)
              : null,
          })),

        pickupPoints: pickUpPlaces.map((p) => ({
          location: p.location,
          date: p.date,
          hour: p.hour || null,
          scheduleType: p.scheduleType === "WINDOW" ? "WINDOW" : "FIXED",
          hourEnd: p.scheduleType === "WINDOW" ? (p.hourEnd || null) : null,
          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          reference: String(p.reference || "").trim() || null, // 🆕
        })),

        deliveryPoints: deliveryPlaces.map((p) => ({
          location: p.location,
          date: p.date,
          hour: p.hour || null,
          scheduleType: p.scheduleType === "WINDOW" ? "WINDOW" : "FIXED",
          hourEnd: p.scheduleType === "WINDOW" ? (p.hourEnd || null) : null,
          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          reference: String(p.reference || "").trim() || null, // 🆕
        })),

        specialLoad,

        pickupLocation: firstPickup?.location || "",
        pickupDate: firstPickup?.date || null,
        pickupHour: firstPickup?.hour || null,
        pickupPostalCode: firstPickup?.postalCode || "",
        pickupCountry: firstPickup?.country || "",
        pickupAddress: firstPickup?.address || "",
        pickupCity: firstPickup?.city || "",
        pickupGMapsLink: firstPickup?.gMapsLink || "",
        pickupCoordinates: firstPickup?.coordinates || "",
        pickupContactPhone: firstPickup?.contactPhone || "",

        deliveryLocation: lastDelivery?.location || "",
        deliveryDate: lastDelivery?.date || null,
        deliveryHour: lastDelivery?.hour || null,
        deliveryPostalCode: lastDelivery?.postalCode || "",
        deliveryCountry: lastDelivery?.country || "",
        deliveryAddress: lastDelivery?.address || "",
        deliveryCity: lastDelivery?.city || "",
        deliveryGMapsLink: lastDelivery?.gMapsLink || "",
        deliveryCoordinates: lastDelivery?.coordinates || "",
        deliveryContactPhone: lastDelivery?.contactPhone || "",
      };

      const newPlaceCandidates = collectNewPlaceCandidates();

      if (newPlaceCandidates.length > 0) {
        setSaveNewPlacesModal({
          visible: true,
          candidates: newPlaceCandidates,
          payload,
          saving: false,
        });

        setLoading(false);
        return;
      }

      await submitLoadPayload(payload);
      return;
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      console.error("🚨 Error creating Load (axios):", {
        status,
        url: err?.config?.url,
        method: err?.config?.method,
        data,
        message: err?.message,
      });

      // ajuda MUITO quando data é object (Sequelize errors etc.)
      try {
        console.error("🚨 Error data pretty:", JSON.stringify(data, null, 2));
      } catch {}

      setMessage(
        data?.message || data?.error || t("errors.serverErrorTryAgain")
      );
    }

    setLoading(false);
    setAlreadySubmitted(false);
  };

  const handleAnalyzeTransportOrder = async (files) => {
    const importRun = ++activeAiImportRunRef.current;
    setCoordinateWorkflowStops([]);

    const incomingFiles = Array.isArray(files) ? files : [];

    const realFiles = incomingFiles
      .map((entry) => {
        if (entry instanceof File) {
          return entry;
        }

        if (entry?.file instanceof File) {
          return entry.file;
        }

        return null;
      })
      .filter(Boolean);

    console.log("🤖 Transport order files before upload:", {
      receivedCount: incomingFiles.length,
      realFileCount: realFiles.length,
      files: realFiles.map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
      })),
    });

    if (!realFiles.length) {
      throw new Error(
        "No valid File objects were received from the uploader."
      );
    }

    if (
      realFiles.length !== 1
    ) {
      throw new Error(
        "LOADEvo Intelligence currently analyses one document at a time."
      );
    }

    const importFile =
      realFiles[0];

    const formData = new FormData();

    realFiles.forEach((file) => {
      formData.append(
        "files",
        file,
        file.name
      );
    });

    let preliminaryApplied = false;
    let preliminarySnapshot = null;

    const applyImportedDraft = (result, { applyToForm = true } = {}) => {
      const draft = result?.draft;

      console.log(
        "🤖 AI transport order result:",
        result
      );

      if (!draft || typeof draft !== "object") {
        throw new Error(
          "LOADEvo Intelligence returned no usable load draft."
        );
      }

      const pickupDrafts = Array.isArray(draft.pickupLocations)
        ? draft.pickupLocations
        : [];

      const deliveryDrafts = Array.isArray(draft.deliveryLocations)
        ? draft.deliveryLocations
        : [];

      const unknownStops = Array.isArray(draft.unknownStops)
        ? draft.unknownStops
        : [];

      if (
        pickupDrafts.length > MAX_POINTS ||
        deliveryDrafts.length > MAX_POINTS
      ) {
        throw new Error(
          `The document contains more than ${MAX_POINTS} pickup or delivery points.`
        );
      }

      if (
        pickupDrafts.length === 0 &&
        deliveryDrafts.length === 0
      ) {
        throw new Error(
          "No pickup or delivery points could be extracted from the document."
        );
      }


      // V9_TEST_REVIEW_OPTIONS_VISIBLE
      // Present Google review candidates without accepting GPS.
      const buildImportReviewGeo = (point = {}) => {
        const geo = point.aiGeocode;

        if (!geo || typeof geo !== "object") {
          return geo ?? null;
        }

        if (
          geo.status !== "MATCH_WITH_CONFLICT" ||
          geo.requiresReview !== true ||
          String(point.coordinates || "").trim()
        ) {
          return geo;
        }

        if (
          (Array.isArray(geo.suggestionOptions) &&
            geo.suggestionOptions.length > 0) ||
          (Array.isArray(geo.candidateOptions) &&
            geo.candidateOptions.length > 0)
        ) {
          return geo;
        }

        const parts = String(
          geo.candidateCoordinates || ""
        ).split(",").map(v => v.trim());

        if (
          parts.length !== 2 ||
          parts.some(v =>
            !/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(v)
          )
        ) {
          return geo;
        }

        const lat = Number(parts[0]);
        const lng = Number(parts[1]);

        if (
          !Number.isFinite(lat) ||
          !Number.isFinite(lng) ||
          Math.abs(lat) > 90 ||
          Math.abs(lng) > 180 ||
          (lat === 0 && lng === 0)
        ) {
          return geo;
        }

        const coordinates = `${lat},${lng}`;

        return {
          ...geo,
          suggestionOptions: [{
            name:
              geo.displayName ||
              point.placeName ||
              point.location ||
              "Localização possível",
            resolvedAddress: geo.resolvedAddress || null,
            coordinates,
            gMapsLink:
              "https://www.google.com/maps?q=" +
              encodeURIComponent(coordinates),
            score: geo.score ?? null,
            conflicts: Array.isArray(geo.reasonCodes)
              ? geo.reasonCodes.filter(code =>
                  String(code).endsWith("_CONFLICT")
                )
              : [],
            confidence: "LOW",
            reviewStatus: "PENDING",
            verified: false,
            requiresReview: true,
            selectableAsVerified: false,
            source: "GOOGLE_REVIEW_CANDIDATE"
          }]
        };
      };

      const mapDraftPoint = (point = {}) => ({
        location:
          point.location ||
          point.placeName ||
          "",

        date:
          point.date ||
          "",

        hour:
          point.hour ||
          "",

        scheduleType:
          point.scheduleType === "WINDOW"
            ? "WINDOW"
            : "FIXED",

        hourEnd:
          point.scheduleType === "WINDOW"
            ? (point.hourEnd || null)
            : null,

        length:
          point.length == null
            ? ""
            : String(point.length),

        width:
          point.width == null
            ? ""
            : String(point.width),

        height:
          point.height == null
            ? ""
            : String(point.height),

        placeId:
          point.placeId == null
            ? null
            : String(point.placeId),

        address:
          point.address ||
          "",

        houseNumber:
          point.houseNumber ||
          "",

        postalCode:
          point.postalCode ||
          "",

        city:
          point.city ||
          "",

        country:
          point.country ||
          "",

        gMapsLink:
          point.gMapsLink ||
          "",

        coordinates:
          point.coordinates ||
          "",

        contactPhone:
          point.contactPhone ||
          "",

        workingHours:
          point.workingHours ??
          null,

        isNewPlace:
          point.isNewPlace === true,

        loadingAccess:
          point.loadingAccess ??
          null,

        handlingEquipment:
          point.handlingEquipment ??
          null,

        handlingEquipmentOther:
          point.handlingEquipmentOther ||
          "",

        reference:
          point.reference ||
          "",

        showMoreFields: true,

        aiDateText:
          point.dateText ||
          null,

        aiTimeText:
          point.timeText ||
          null,

        aiConfidence:
          point.aiConfidence ??
          null,

        aiEvidence:
          point.aiEvidence ||
          null,

        aiGeocode:
          buildImportReviewGeo(point),

        aiPlaceResolution:
          point.aiPlaceResolution ??
          null,

        aiLocationResolution:
          point.aiLocationResolution ??
          null,

        locationError: false,
        dateError: false,
        hourError: false,
        addressError: false,
        postalCodeError: false,
        cityError: false,
        countryError: false,
        gMapsLinkError: false,
        coordinatesError: false,
        contactPhoneError: false,
      });

      const aiPickupPlaces =
        pickupDrafts.map(mapDraftPoint);

      const aiDeliveryPlaces =
        deliveryDrafts.map(mapDraftPoint);

      if (applyToForm) {
      setCoordinateWorkflowStops([
        ...aiPickupPlaces.map(
          (point, index) => ({
            key:
              `pickup-${index}`,

            type:
              "PICKUP",

            index,

            point,
          })
        ),

        ...aiDeliveryPlaces.map(
          (point, index) => ({
            key:
              `delivery-${index}`,

            type:
              "DELIVERY",

            index,

            point,
          })
        ),
      ]);

      }

      const aiLoadLength =
        Number(
          draft?.loadDimensions?.length
        );

      /*
       * A single pickup and a single delivery
       * represent the same cargo.
       *
       * Therefore:
       * pickup total = delivery total = load length.
       *
       * We only auto-distribute a global LDM value
       * when the distribution is unambiguous.
       */
      if (
        Number.isFinite(
          aiLoadLength
        ) &&
        aiLoadLength > 0 &&
        aiPickupPlaces.length === 1 &&
        aiDeliveryPlaces.length === 1
      ) {
        if (
          String(
            aiPickupPlaces[0]
              ?.length ?? ""
          ).trim() === ""
        ) {
          aiPickupPlaces[0].length =
            String(
              aiLoadLength
            );
        }

        if (
          String(
            aiDeliveryPlaces[0]
              ?.length ?? ""
          ).trim() === ""
        ) {
          aiDeliveryPlaces[0].length =
            String(
              aiLoadLength
            );
        }
      }

      // Produce identical normalized points without replacing any form state.
      if (!applyToForm) {
        return { pickups: aiPickupPlaces, deliveries: aiDeliveryPlaces };
      }

      if (aiPickupPlaces.length > 0) {
        setPickUpPlaces(
          aiPickupPlaces.map((point) => ({
            ...point,
            _uiId: createStopUiId(),
          }))
        );
        setActivePickUpIndex(0);
      }

      if (aiDeliveryPlaces.length > 0) {
        setDeliveryPlaces(
          aiDeliveryPlaces.map((point) => ({
            ...point,
            _uiId: createStopUiId(),
          }))
        );
        setActiveDeliveryIndex(0);
      }

      if (draft.weight != null) {
        setWeightInput(
          String(draft.weight)
        );
      }

      if (
        typeof draft.merchandise === "string" &&
        draft.merchandise.trim()
      ) {
        setMerchandise(
          draft.merchandise.trim()
        );
      }

      if (
        typeof draft.trailerType === "string" &&
        draft.trailerType.trim()
      ) {
        setTrailerType(
          draft.trailerType.trim().toUpperCase()
        );
      }

      if (
        typeof draft.trailerTypeOther === "string" &&
        draft.trailerTypeOther.trim()
      ) {
        setTrailerTypeOther(
          draft.trailerTypeOther.trim()
        );
      }

      if (draft.price != null) {
        setPriceInput(
          String(draft.price)
        );
      }

      if (
        typeof draft.priceCurrency === "string" &&
        draft.priceCurrency.trim()
      ) {
        setPriceCurrency(
          draft.priceCurrency.trim().toUpperCase()
        );
      }

      if (
        typeof draft.notes4driver === "string" &&
        draft.notes4driver.trim()
      ) {
        setNotes4driver(
          draft.notes4driver.trim()
        );
      }

      setInvalidFields({});
      setDateFieldErrors({});
      setPickUpPlaceError("");
      setDeliveryPlaceError("");
      setGlobalHourFormatError(false);

      setWeightError("");
      setMerchandiseError("");
      setTrailerTypeError("");
      setTrailerTypeOtherError("");
      setPriceError("");
      setNotes4driverError("");
      setError("");
      setAlreadySubmitted(false);

      if (
        result?.requiresReview ===
        true
      ) {
        console.warn(
          "⚠️ AI import requires manual review:",
          result?.reasonCodes ||
          []
        );

        setMessage(
          "The document was analysed and the fields were filled, but some values need manual review before creating the load."
        );

      } else if (
        unknownStops.length > 0
      ) {
        console.warn(
          "⚠️ AI returned operational stops requiring manual classification:",
          unknownStops
        );

        setMessage(
          `${unknownStops.length} operational stop(s) need manual review because their type could not be determined.`
        );

      } else {
        setMessage("");
      }

      console.log(
        "✅ CreateLoad pre-filled from transport order:",
        {
          pickupCount: aiPickupPlaces.length,
          deliveryCount: aiDeliveryPlaces.length,
          unknownCount: unknownStops.length,
          weight: draft.weight,
          merchandise: draft.merchandise,
          trailerType: draft.trailerType,
          price: draft.price,
        }
      );

      return { pickups: aiPickupPlaces, deliveries: aiDeliveryPlaces };
    };

    try {
      setAiImportJob({
        status:
          "QUEUED",

        stage:
          "UPLOAD_RECEIVED",

        progress:
          0,

        requiresReview:
          null,

        fileName:
          importFile?.name ||
          "Transport order",

        fileSize:
          importFile?.size ||
          null,

        fileType:
          importFile?.type ||
          "",
      });

      setAtlasCollapsed(false);
      setAiJourneyScrollRun(importRun);

      const response =
        await importTransportOrderAsync(
          formData,
          {
            onProgress:
              (job) => {
                if (activeAiImportRunRef.current !== importRun) return;
                setAiImportJob(
                  (previous) => ({
                    ...(
                      previous ||
                      {}
                    ),

                    ...(
                      job ||
                      {}
                    ),

                    fileName:
                      previous?.fileName ||
                      importFile?.name ||
                      "Transport order",

                    fileSize:
                      previous?.fileSize ??
                      importFile?.size ??
                      null,

                    fileType:
                      previous?.fileType ||
                      importFile?.type ||
                      "",
                  })
                );
              },
            onDraftReady: (preliminaryResult) => {
              if (activeAiImportRunRef.current !== importRun || preliminaryApplied) return;
              const snapshot = applyImportedDraft(preliminaryResult);
              preliminarySnapshot = snapshot;
              preliminaryApplied = true;
            },
          }
        );

      if (activeAiImportRunRef.current !== importRun) {
        return response.data;
      }

      if (!preliminaryApplied) {
        // Backward-compatible: old backend only returns a final draft.
        applyImportedDraft(response.data);
      } else {
        const finalSnapshot = applyImportedDraft(
          response.data, { applyToForm: false }
        );
        setPickUpPlaces(previous => mergeFinalImportLocations(
          previous, preliminarySnapshot.pickups, finalSnapshot.pickups
        ));
        setDeliveryPlaces(previous => mergeFinalImportLocations(
          previous, preliminarySnapshot.deliveries, finalSnapshot.deliveries
        ));
        if (response.data?.requiresReview) {
          setMessage(
            "Location analysis finished. Some places need manual review. " +
            "Changes you made while waiting have been preserved."
          );
        } else {
          setMessage(
            "Location analysis finished. Please confirm any locations " +
            "you manually changed while the resolver was working."
          );
        }
      }

      return response.data;
    } catch (error) {
      if (activeAiImportRunRef.current !== importRun) throw error;
      console.error(
        "❌ Error analysing transport order:",
        {
          status:
            error?.response?.status,

          data:
            error?.response?.data,

          message:
            error?.message,

          importJobId:
            error?.importJobId ||
            null,
        }
      );

      setAiImportJob(
        (previous) => ({
          ...(
            previous ||
            {}
          ),

          id:
            error?.importJobId ||
            previous?.id ||
            null,

          status:
            "FAILED",

          stage:
            "FAILED",

          error: {
            code:
              error?.code ||
              error?.response
                ?.data
                ?.code ||
              null,

            message:
              error?.response
                ?.data
                ?.error ||
              error?.response
                ?.data
                ?.message ||
              error?.message ||
              "The document could not be analysed.",
          },
        })
      );

      throw error;
    }
  };

  const scrollToLoadDetails = () => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    loadDetailsRef.current?.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <div className="create-load-container create-load-atlas-layout">

      {successPopupOpen && (
        <div
          className="create-load-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-load-success-title"
        >
          <div className="create-load-popupModal">
            <div className="create-load-popupIconWrap">
              <FaCheckCircle className="create-load-popupIcon" />
            </div>

            <h2
              id="create-load-success-title"
              className="create-load-popupTitle"
            >
              {t("popup.created_title")}
            </h2>

            <p className="create-load-popupMessage">
              {t("popup.created_message")}
            </p>

            <div className="create-load-popupActions">
              <button
                type="button"
                className="create-load-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal para guardar Places novos */}
      {saveNewPlacesModal.visible && (
        <div className="create-load-modal-overlay">
          <div className="create-load-modal-box">
            <h3>
              {saveNewPlacesModal.candidates.length === 1
                ? "Guardar novo local?"
                : "Guardar novos locais?"}
            </h3>

            <p>
              {saveNewPlacesModal.candidates.length === 1
                ? "Este local ainda não existe nos Places. Queres guardá-lo para usar novamente no futuro?"
                : "Foram encontrados locais que ainda não existem nos Places. Queres guardá-los para usar novamente no futuro?"}
            </p>

            <div style={{ textAlign: "left", margin: "14px 0" }}>
              {saveNewPlacesModal.candidates.map((candidate) => (
                <div
                  key={candidate.key}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 10,
                    background: "rgba(255,255,255,0.06)",
                    marginBottom: 8,
                    fontSize: 14,
                  }}
                >
                  <strong>{candidate.label}</strong>
                  <div style={{ opacity: 0.75, fontSize: 12 }}>
                    {candidate.payload.address}, {candidate.payload.postalCode}{" "}
                    {candidate.payload.city}, {candidate.payload.country}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="create-load-modal-button"
                disabled={saveNewPlacesModal.saving}
                onClick={() => {
                  setSaveNewPlacesModal({
                    visible: false,
                    candidates: [],
                    payload: null,
                    saving: false,
                  });
                  setLoading(false);
                  setAlreadySubmitted(false);
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="create-load-modal-button"
                disabled={saveNewPlacesModal.saving}
                onClick={handleCreateWithoutSavingNewPlaces}
              >
                Não guardar
              </button>

              <button
                type="button"
                className="create-load-modal-button"
                disabled={saveNewPlacesModal.saving}
                onClick={handleSaveNewPlacesAndCreateLoad}
              >
                {saveNewPlacesModal.saving ? "A guardar..." : "Guardar e criar carga"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed time / Time window — date remains in the form */}
      {timeSchedulePopup && (
        <CreateLoadTimeSchedulePicker
          key={`${timeSchedulePopup.kind}:${timeSchedulePopup.id}`}
          kind={timeSchedulePopup.kind}
          initialType={timeSchedulePopup.scheduleType}
          initialStart={timeSchedulePopup.hour}
          initialEnd={timeSchedulePopup.hourEnd}
          position={{
            top: timeSchedulePopup.top,
            left: timeSchedulePopup.left,
          }}
          onClose={() => setTimeSchedulePopup(null)}
          onApply={applyTimeSchedule}
        />
      )}

      {/* Schedule conflict resolver */}
      {scheduleEditor && (() => {
        const points =
          scheduleEditor.kind === "pickup"
            ? pickUpPlaces
            : deliveryPlaces;

        const issue = findStopSequenceConflict(points);

        if (!issue) return null;

        const first = points[issue.beforeIndex];
        const second = points[issue.afterIndex];

        const selectedIndex = points.findIndex(
          (point) => point._uiId === scheduleEditor.id
        );

        const selected = points[selectedIndex];
        const previous = points[selectedIndex - 1];
        const next = points[selectedIndex + 1];

        if (!selected) return null;

        return (
          <div className="create-load-modal-overlay">
            <div
              className="create-load-modal-box loadevo-reorder-schedule-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="loadevo-reorder-modal-title"
            >
              <div className="loadevo-reorder-modal-heading">
                <FiClock aria-hidden="true" />

                <div>
                  <h3 id="loadevo-reorder-modal-title">
                    Schedule conflict
                  </h3>

                  <p>
                    The current stop order does not match
                    the scheduled times.
                  </p>
                </div>
              </div>

              <div className="loadevo-reorder-conflict-stops">
                {[first, second].map((point, index) => (
                  <React.Fragment key={point._uiId}>
                    {index === 1 && (
                      <div className="loadevo-reorder-conflict-separator">
                        <FiChevronDown aria-hidden="true" />
                        <span>Then</span>
                      </div>
                    )}

                    <div className="loadevo-reorder-conflict-stop">
                      <span className="loadevo-reorder-conflict-number">
                        {index === 0
                          ? issue.beforeIndex + 1
                          : issue.afterIndex + 1}
                      </span>

                      <div className="loadevo-reorder-conflict-name">
                        <strong>
                          {point.location || "Unnamed stop"}
                        </strong>

                        <small>
                          {point.city || (
                            scheduleEditor.kind === "pickup"
                              ? "Pickup"
                              : "Delivery"
                          )}
                        </small>
                      </div>

                      <span className="loadevo-reorder-conflict-time">
                        {formatReorderSchedule(point)}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <p className="loadevo-reorder-conflict-description">
                <strong>
                  {first.location || "The first stop"}
                </strong>
                {" "}is scheduled after{" "}
                <strong>
                  {second.location || "the next stop"}
                </strong>
                , despite appearing before it in the route order.
              </p>

              <div
                className="loadevo-reorder-choices"
                role="group"
                aria-label="Choose which stop to adjust"
              >
                {[first, second].map((point) => (
                  <button
                    key={point._uiId}
                    type="button"
                    className={`loadevo-reorder-choice${
                      scheduleEditor.id === point._uiId
                        ? " is-selected"
                        : ""
                    }`}
                    aria-pressed={
                      scheduleEditor.id === point._uiId
                    }
                    onClick={() =>
                      chooseScheduleStop(point._uiId)
                    }
                  >
                    <strong>
                      Change {point.location || "this stop"}
                    </strong>

                    <small>
                      {formatReorderSchedule(point)}
                    </small>
                  </button>
                ))}
              </div>

              <div className="loadevo-reorder-edit-panel">
                <div className="loadevo-reorder-edit-heading">
                  Editing:{" "}
                  <strong>
                    {selected.location || "Unnamed stop"}
                  </strong>
                </div>

                <div className="loadevo-reorder-schedule-fields">
                  <label>
                    Date

                    <input
                      type="date"
                      value={scheduleEditor.date}
                      onChange={(event) =>
                        setScheduleEditor((old) => ({
                          ...old,
                          date: event.target.value,
                          error: "",
                        }))
                      }
                    />
                  </label>

                  <label>
                    Time

                    <input
                      type="time"
                      value={scheduleEditor.hour}
                      onChange={(event) =>
                        setScheduleEditor((old) => ({
                          ...old,
                          hour: event.target.value,
                          error: "",
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="loadevo-reorder-time-limits">
                  {previous && stopScheduleValue(previous) && (
                    <span>
                      Not before{" "}
                      {formatReorderSchedule(previous)}
                      {" "}({previous.location || "previous stop"}).
                    </span>
                  )}

                  {next && stopScheduleValue(next) && (
                    <span>
                      Not after{" "}
                      {formatReorderSchedule(next)}
                      {" "}({next.location || "next stop"}).
                    </span>
                  )}

                  <small>
                    These limits do not include travel
                    or loading time.
                  </small>
                </div>
              </div>

              {scheduleEditor.error && (
                <p
                  className="loadevo-reorder-schedule-error"
                  role="alert"
                >
                  {scheduleEditor.error}
                </p>
              )}

              <div className="loadevo-reorder-modal-actions">
                <button
                  type="button"
                  onClick={revertStopOrder}
                >
                  Revert move
                </button>

                <button
                  type="button"
                  onClick={() => setScheduleEditor(null)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="is-primary"
                  onClick={saveScheduleEditor}
                >
                  Save schedule
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* ✅ Modal para erro de data inválida */}
      {dateErrorModal.visible && (
        <div className="create-load-modal-overlay">
          <div className="create-load-modal-box">
            <h3>{t("modal.invalidDate.title")}</h3>
            <p>{t("modal.invalidDate.body")}</p>
            <button
              onClick={() => {
                const { type, index } = dateErrorModal;
                if (type === "pickup") {
                  const updated = [...pickUpPlaces];
                  updated[index].date = "";
                  setPickUpPlaces(updated);
                } else if (type === "delivery") {
                  const updated = [...deliveryPlaces];
                  updated[index].date = "";
                  setDeliveryPlaces(updated);
                }
                setDateErrorModal({ visible: false, type: "", index: null });
                setAlreadySubmitted(false);
                setLoading(false);
              }}
              className="create-load-modal-button"
            >
              {t("actions.ok")}
            </button>
          </div>
        </div>
      )}

      <header className="create-load-atlas-heading loadevo-v7-page-heading">
        <div className="loadevo-v7-heading-copy">
          <div className="loadevo-v7-page-title-row">
            <FiTruck aria-hidden="true" />

            <div>
              <h2 className="create-load-title">
                {t("title")}
              </h2>

              <p className="create-load-atlas-lead">
                {t("atlas.pageDescription", {
                  defaultValue:
                    "Add the load details, multiple stops and plan your transport.",
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="loadevo-v7-page-actions">
          <button
            type="button"
            className="loadevo-v7-create-button"
            onClick={handleCreateLoad}
            disabled={
              loading ||
              initializingSession ||
              alreadySubmitted
            }
          >
            {loading
              ? t("actions.creating")
              : t("actions.createLoad")}
          </button>

        </div>
      </header>

      <div
        className={`loadevo-v6-page-grid${
          atlasCollapsed ? " atlas-is-collapsed" : ""
        }`}
      >
        <div className="loadevo-v6-page-main">
      <section
        className="loadevo-v6-ai-import"
        aria-label="Import transport order with AI"
      >
        <div className="loadevo-v6-ai-brand">
          <img
            className="loadevo-v6-ai-robot"
            src={aiImportHeroRobot}
            alt=""
          />

          <div>
            <span className="loadevo-v6-ai-badge">
              LOADEvo AI
            </span>

            <h3 className="loadevo-v6-ai-title">
              Import load details automatically
            </h3>

            <p className="loadevo-v6-ai-caption">
              Upload your transport order. Review the
              extracted details while locations are verified.
            </p>
          </div>
        </div>

        <div className="loadevo-v6-ai-uploader">
          <LoadDocumentUploader
            onAnalyze={handleAnalyzeTransportOrder}
            compact
          />
        </div>

        <div
          className="loadevo-v6-ai-features"
          aria-label="AI import capabilities"
        >
          <span>
            <FiFileText aria-hidden="true" />
            Read document
          </span>

          <span>
            <FiLink aria-hidden="true" />
            Verify locations
          </span>

          <span>
            <FiCheck aria-hidden="true" />
            Prefill editable fields
          </span>
        </div>
      </section>

      <div className="load-ai-import-divider">
        <span>
          {t("aiImport.manualDivider", {
            defaultValue: "or enter the load manually",
          })}
        </span>
      </div>

      <div
        ref={loadDetailsRef}
        className="create-load-atlas-workspace"
      >
        <main className="create-load-form create-load-atlas-form">
          <section className="create-load-atlas-section create-load-atlas-order">
            <div className="create-load-atlas-order-header">
              <h3 className="create-load-column-title">
                {t("atlas.orderDetails", { defaultValue: "Order details" })}
              </h3>

              <div className="create-load-atlas-order-header-toggles">
                <div className="create-load-atlas-header-toggle">
                  <span className="create-load-label">
                    {t("fields.loadBearer")}
                  </span>
                  <label className="load-switch">
                    <input
                      type="checkbox"
                      aria-label={t("fields.loadBearer")}
                      checked={loadBearer}
                      onChange={() => {
                        setLoadBearer(!loadBearer);
                        if (!loadBearer) setSpecialLoad(false);
                      }}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                {!existingTransportSessionId && (
                  <div className="create-load-atlas-header-toggle">
                    <span className="create-load-label">
                      {t("fields.specialLoad")}
                    </span>
                    <label className="load-switch">
                      <input
                        type="checkbox"
                        aria-label={t("fields.specialLoad")}
                        checked={specialLoad}
                        onChange={() => {
                          setSpecialLoad(!specialLoad);
                          if (!specialLoad) setLoadBearer(false);
                        }}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                )}
              </div>
            </div>

        {/* Premium V6 — Independent Order Details grid */}
          <div className="loadevo-v6-order-fields">
            {/* 💰 Price (required) */}
            <div className="create-load-field-group create-load-atlas-order-price">
              <label className="create-load-label">{t("price.label")}*</label>

              <div className={`create-load-price-wrapper ${priceError ? "input-error" : ""}`}>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={t("price.placeholder")}
                  value={priceInput}
                  onChange={(e) => {
                    const v = normalizeMoney(e.target.value);
                    if (/^\d{0,9}(\.\d{0,2})?$/.test(v) || v === "" || v === ".") {
                      setPriceInput(v === "." ? "0." : v);
                      if (priceError) setPriceError("");
                    }
                  }}
                  onBlur={() => {
                    if (priceInput === "" || priceInput == null) {
                      setPriceError(
                        t("errors.priceRequired", {
                          defaultValue: "❌ Price is required.",
                        })
                      );
                      return;
                    }

                    const n = Number(priceInput);

                    if (Number.isNaN(n) || n < 0) {
                      setPriceError(t("errors.priceMustBePositive"));
                    } else {
                      setPriceInput(n.toFixed(2));
                      setPriceError("");
                    }
                  }}
                  className="create-load-input create-load-price-input"
                />

                <div className="create-load-price-currency">
                  <Select
                    options={[
                      { value: "EUR", label: "EUR" },
                      { value: "USD", label: "USD" },
                      { value: "GBP", label: "GBP" },
                    ]}
                    value={{ value: priceCurrency, label: priceCurrency }}
                    onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                    isSearchable={false}
                    styles={customSelectStyles}
                    classNamePrefix="select"
                  />
                </div>
              </div>

              {priceError && (
                <p className="create-load-error-text" style={{ marginTop: 4 }}>
                  {priceError}
                </p>
              )}

              {priceInput && !priceError && <div className="create-load-price-preview">{formatPrice(priceInput, priceCurrency)}</div>}
            </div>

                <div className="create-load-field-group create-load-client-type-field">
                  <label className="create-load-label">{t("clientType.label")}*</label>

                  <Select
                    className="create-load-fullwidth-select"
                    options={[
                      { value: "industry", label: t("clientType.industry") },
                      { value: "carrier", label: t("clientType.carrier") },
                    ]}
                    value={
                      clientType
                        ? {
                            value: clientType,
                            label: clientType === "industry" ? t("clientType.industry") : t("clientType.carrier"),
                          }
                        : null
                    }
                    onChange={(selected) => {
                      setClientType(selected?.value || "");
                      setClientTypeError(false);
                      setSelectedClient(null);
                      setSelectedClientContactEmails([]);
                    }}
                    placeholder={t("clientType.placeholder")}
                    styles={customSelectStyles}
                    isClearable
                    isSearchable={false}
                    classNamePrefix={clientTypeError ? "select-error" : "select"}
                  />

                  {clientTypeError && (
                    <p className="create-load-dropdown-field-error">{t("errors.selectClientType")}</p>
                  )}                </div>

                <div className="create-load-field-group create-load-client-dependent-field">
                  <label className="create-load-label">
                    {clientType === "industry"
                      ? `${t("client.labelIndustry")}*`
                      : clientType === "carrier"
                        ? `${t("client.labelCarrier")}*`
                        : `${t("client.labelIndustry", { defaultValue: "Client" })}*`}
                  </label>

                  <Select
                    className="create-load-fullwidth-select"
                    value={selectedClient}
                    onChange={(selected) => {
                      setSelectedClient(selected);
                      setSelectedClientError(false);
                      setSelectedClientContactEmails([]);
                    }}
                    options={
                      clientType === "industry"
                        ? industryClients.map((c) => ({
                            value: c.id,
                            label: String(c.companyName || c.name || "").trim(),
                          }))
                        : clientType === "carrier"
                          ? carrierClients.map((c) => ({
                              value: c.id,
                              label: String(c.companyName || c.name || "").trim(),
                            }))
                          : []
                    }
                    styles={customSelectStyles}
                    placeholder={
                      clientType
                        ? t("client.placeholder")
                        : t("clientType.placeholder")
                    }
                    isClearable
                    isDisabled={!clientType}
                    classNamePrefix={selectedClientError ? "select-error" : "select"}
                  />

                  {selectedClientError && (
                    <p className="create-load-dropdown-field-error">{t("errors.selectClient")}</p>
                  )}
                </div>

                <div className="create-load-field-group create-load-client-dependent-field">
                  <label className="create-load-label">
                    {t("fields.clientContactEmails.label", {
                      defaultValue: "Client contact email(s)",
                    })}
                  </label>

                  <Select
                    className="create-load-fullwidth-select"
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    options={selectedClient ? selectedClientContactEmailOptions : []}
                    value={selectedClientContactEmails}
                    onChange={(selectedOptions) => {
                      const limited = Array.isArray(selectedOptions) ? selectedOptions.slice(0, 3) : [];
                      setSelectedClientContactEmails(limited);
                    }}
                    placeholder={t("fields.clientContactEmails.placeholder", {
                      defaultValue: "Select up to 3 contact email(s)...",
                    })}
                    noOptionsMessage={() =>
                      selectedClient
                        ? t("fields.clientContactEmails.noOptions", {
                            defaultValue: "No contact emails found for this client.",
                          })
                        : t("client.placeholder", {
                            defaultValue: "Search or select a client",
                          })
                    }
                    styles={multiSelectHorizontalStyles}
                    isDisabled={!selectedClient}
                    classNamePrefix="select"
                  />

                </div>

                {/* ✅ Transport Carrier */}
                <div className="create-load-field-group create-load-client-dependent-field">
                  <label className="create-load-label">{t("transportCarrier.label")}</label>

                  <Select
                    className="create-load-fullwidth-select"
                    options={availableCarriers}
                    value={availableCarriers.find((c) => String(c.value) === String(transportCarrierId)) || null}
                    onChange={(selected) => {
                      setTransportCarrierId(selected?.value ? String(selected.value) : null);
                      setCarrierError(false);
                      setSelectedSubcontractedEmails([]);
                    }}
                    isClearable
                    placeholder={t("transportCarrier.placeholder")}
                    styles={carrierSelectStyles}
                    classNamePrefix={carrierError ? "select-error" : "select"}
                    filterOption={(candidate, input) => {
                      const term = String(input || "").toLowerCase().trim();
                      if (!term) return true;
                      const data = candidate?.data || {};
                      return String(data.searchText || data.label || "").toLowerCase().includes(term);
                    }}
                  />

                  {carrierError && (
                    <p className="create-load-dropdown-field-error">{t("errors.selectTransportCarrier")}</p>
                  )}
                </div>

                {showNotes4subcontracted && (
                  <div className="create-load-field-group create-load-client-dependent-field">
                    <label className="create-load-label">
                      {t("fields.subcontractedEmail.label")}
                    </label>

                    <Select
                      className="create-load-fullwidth-select"
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      options={selectedSubcontractedEmailOptions}
                      value={selectedSubcontractedEmails}
                      onChange={(selectedOptions) => {
                        const limited = Array.isArray(selectedOptions)
                          ? selectedOptions.slice(0, 3)
                          : [];
                        setSelectedSubcontractedEmails(limited);
                      }}
                      placeholder={t("fields.subcontractedEmail.placeholder")}
                      noOptionsMessage={() => t("fields.subcontractedEmail.noOptions")}
                      styles={multiSelectHorizontalStyles}
                      classNamePrefix="select"
                    />

                  </div>
                )}

                {/* 🆕 Price for Subcontracted (ONLY when external carrier) */}
                {showNotes4subcontracted && (
                  <div className="create-load-field-group create-load-atlas-order-subprice">
                    <label className="create-load-label">
                      {t("fields.price4subcontracted.label")}
                    </label>

                    <div
                      className={`create-load-price-wrapper ${
                        price4subcontractedError ? "input-error" : ""
                      }`}
                    >
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder={t("fields.price4subcontracted.placeholder")}
                        value={price4subcontractedInput}
                        onChange={(e) => {
                          const v = normalizeMoney(e.target.value);
                          if (/^\d{0,9}(\.\d{0,2})?$/.test(v) || v === "" || v === ".") {
                            setPrice4subcontractedInput(v === "." ? "0." : v);
                            if (price4subcontractedError) setPrice4subcontractedError("");
                          }
                        }}
                        onBlur={() => {
                          if (price4subcontractedInput === "" || price4subcontractedInput == null) {
                            setPrice4subcontractedError("");
                            return;
                          }
                          const n = Number(price4subcontractedInput);
                          if (Number.isNaN(n) || n < 0) {
                            setPrice4subcontractedError(t("errors.priceMustBePositive"));
                          } else {
                            setPrice4subcontractedInput(n.toFixed(2));
                            setPrice4subcontractedError("");
                          }
                        }}
                        className="create-load-input create-load-price-input"
                      />

                      <div className="create-load-price-currency">
                        <Select
                          options={[
                            { value: "EUR", label: "EUR" },
                            { value: "USD", label: "USD" },
                            { value: "GBP", label: "GBP" },
                          ]}
                          value={{ value: priceCurrency, label: priceCurrency }}
                          isDisabled
                          isSearchable={false}
                          styles={customSelectStyles}
                          classNamePrefix="select"
                        />
                      </div>
                    </div>

                    {price4subcontractedError && (
                      <p className="create-load-error-text" style={{ marginTop: 4 }}>
                        {price4subcontractedError}
                      </p>
                    )}

                    {price4subcontractedInput && !price4subcontractedError && (
                      <div className="create-load-price-preview">
                        {formatPrice(price4subcontractedInput, priceCurrency)}
                      </div>
                    )}
                  </div>
                )}

                {/* 🆕 Trailer Type — Order Details */}
                <div className="create-load-field-group create-load-trailer-type-field">
                  <label className="create-load-label">
                    {t("fields.trailerType.label", { defaultValue: "Trailer type" })}
                  </label>

                  <Select
                    className="create-load-fullwidth-select"
                    options={trailerTypeOptions}
                    value={trailerTypeOptions.find((o) => o.value === trailerType) || null}
                    onChange={(opt) => {
                      const v = opt?.value || null;
                      setTrailerType(v);

                      // ✅ if not OTHER, clear free text
                      if (v !== "OTHER") setTrailerTypeOther("");

                      if (trailerTypeError) setTrailerTypeError("");
                      if (trailerTypeOtherError) setTrailerTypeOtherError("");
                    }}
                    isClearable
                    placeholder={t("fields.trailerType.placeholder", { defaultValue: "Select trailer type..." })}
                    styles={customSelectStyles}
                    classNamePrefix={trailerTypeError ? "select-error" : "select"}
                  />

                  {trailerTypeError && (
                    <p className="create-load-error-text">
                      {trailerTypeError}
                    </p>
                  )}
                </div>

                {trailerType === "OTHER" && (
                  <div className="create-load-field-group">
                    <label className="create-load-label">
                      {t("fields.trailerTypeOther.label", { defaultValue: "Other (specify)" })}
                    </label>

                    <input
                      type="text"
                      value={trailerTypeOther || ""}
                      placeholder={t("fields.trailerTypeOther.placeholder", { defaultValue: "Write the trailer type..." })}
                      onChange={(e) => {
                        setTrailerTypeOther(e.target.value);
                        if (trailerTypeOtherError) setTrailerTypeOtherError("");
                      }}
                      className={`create-load-input ${trailerTypeOtherError ? "input-error" : ""}`}
                    />

                    {trailerTypeOtherError && (
                      <p className="create-load-error-text">
                        {trailerTypeOtherError}
                      </p>
                    )}
                  </div>
                )}
              </div>
          </section>


            {/* Column 1: Loading Points */}
            <section className="create-load-atlas-section create-load-atlas-pickup create-load-load-column">
              <div className="loadevo-v6-stops-header">
                <h3 className="create-load-column-title">
                  <span className="create-load-atlas-step loadevo-v6-step-icon">
                    <FiMapPin aria-hidden="true" />
                  </span>
                  <span>Cargas</span>
                </h3>

                <button
                  type="button"
                  className="loadevo-v6-add-stop-btn"
                  onClick={addPickUpPlace}
                  disabled={pickUpPlaces.length >= MAX_POINTS}
                >
                  Adicionar Carga
                </button>
              </div>

              <div className="create-load-field-group">
                <DndContext
                  sensors={stopDndSensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  autoScroll={false}
                  onDragStart={(event) => startStopDrag(event, "pickup")}
                  onDragEnd={(event) => dropOnStop(event, "pickup")}
                  onDragCancel={finishStopDrag}
                >
                  <SortableContext
                    items={pickUpPlaces.map((place) => place._uiId)}
                    strategy={verticalListSortingStrategy}
                  >
                {pickUpPlaces.map((place, index) => (
                  <CreateLoadSortableStop
                    key={place._uiId}
                    id={place._uiId}
                    kind="pickup"
                    index={index}
                    disabled={reorderLocked}
                    dragInProgress={draggingStop?.kind === "pickup"}
                    onArrowMove={(event) =>
                      nudgeStopWithKeyboard(
                        event,
                        "pickup",
                        place._uiId,
                        index
                      )
                    }
                  >
                    <CreateLoadStopCard
                      kind="pickup"
                      index={index}
                      stop={place}
                      initiallyExpanded={
                        !aiImportJob &&
                        (index === 0 || index === pickUpPlaces.length - 1)
                      }
                      importRevision={aiJourneyScrollRun}
                      openRequest={
                        stopOpenRequest.kind === "pickup" &&
                        stopOpenRequest.index === index
                          ? stopOpenRequest.seq
                          : 0
                      }
                      invalid={
                        Boolean(dateFieldErrors[`pickup-${index}`]) ||
                        Boolean(place.hourError) ||
                        Object.keys(invalidFields).some(
                          (key) =>
                            key.startsWith("pickup-") &&
                            key.endsWith(`-${index}`) &&
                            Boolean(invalidFields[key])
                        )
                      }
                      canDelete={pickUpPlaces.length > 1}
                      onDelete={() => removePickUpPlace(index)}
                      onOperationMinutesChange={(value) =>
                        handlePickUpPlaceChange(
                          { target: { value } },
                          index,
                          "operationMinutes"
                        )
                      }
                      onExpand={() =>
                        setPickUpPlaces((previous) => {
                          if (previous[index]?.showMoreFields) {
                            return previous;
                          }

                          return previous.map((item, i) =>
                            i === index
                              ? { ...item, showMoreFields: true }
                              : item
                          );
                        })
                      }
                    >
                      <div className="create-load-input-container loadevo-v8-stop-form">
                        <div className="loadevo-v8-section-heading loadevo-v8-heading--location">
                          <FiMapPin aria-hidden="true" />
                          <span>Location &amp; schedule</span>
                        </div>

                        <div className="loadevo-v8-section-heading loadevo-v8-heading--address">
                          <FiMapPin aria-hidden="true" />
                          <span>Address &amp; geolocation</span>
                        </div>

                        <div className="loadevo-v8-section-heading loadevo-v8-heading--requirements">
                          <FiBox aria-hidden="true" />
                          <span>Loading requirements</span>
                        </div>

                        <div className="loadevo-v8-section-heading loadevo-v8-heading--contact">
                          <FiFileText aria-hidden="true" />
                          <span>Contact &amp; reference</span>
                        </div>

                      <div className="create-load-pickup-label-row">
                        <label className="create-load-label">{t("fields.pickupPlace")}</label>
                        {pickUpPlaces.length > 1 && (
                          <button
                            type="button"
                            className="create-load-pickup-delete-btn"
                            onClick={() => removePickUpPlace(index)}
                            title={t("actions.removePickupPoint")}
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      <div className="select-wrapper-left-icon">
                        <CreatableSelect
                          isClearable
                          isMulti={false}
                          options={placeOptions}
                          placeholder={t("placeSelect.placeholder")}
                          value={
                            place.placeId
                              ? placeOptions.find((p) => p.value === place.placeId) || null
                              : place.location
                              ? { label: place.location, value: place.location }
                              : null
                          }
                          formatOptionLabel={(opt, { context }) =>
                            renderStopLocationOption(opt, context, place)
                          }
                          getOptionValue={(opt) => String(opt.value)}
                          onChange={(selectedOption) => {
                            const updated = [...pickUpPlaces];

                            if (selectedOption?.__isNew__) {
                              updated[index] = renameStopLocationPreservingDetails(
                                updated[index],
                                selectedOption
                              );
                            } else if (selectedOption?.fullData) {
                              const p = selectedOption.fullData;
                              updated[index] = {
                                ...updated[index],
                                placeId: String(p.id),
                                isNewPlace: false, // 🆕 existing place selected
                                location: p.placeName,
                                address: p.address,
                                postalCode: p.postalCode,
                                city: p.city,
                                country: p.country,
                                gMapsLink: p.gMapsLink,
                                coordinates: p.coordinates,
                                contactPhone: p.contactPhone,
                                workingHours: p.workingHours ?? null,
                                loadingAccess: p.loadingAccess || "SIDE",
                                handlingEquipment: p.handlingEquipment || "FORKLIFT", // ✅ default: Empilhador
                                handlingEquipmentOther: p.handlingEquipmentOther || "",
                              };
                            } else {
                              updated[index] = renameStopLocationPreservingDetails(
                                updated[index],
                                selectedOption
                              );
                            }

                            setPickUpPlaces(updated);

                            setInvalidFields((prev) => {
                              const copy = { ...prev };
                              delete copy[`pickup-location-${index}`];
                              const fields = [
                                "location",
                                "address",
                                "postalCode",
                                "city",
                                "country",
                                "gMapsLink",
                                "coordinates",
                                "contactPhone",
                              ];
                              fields.forEach((f) => delete copy[`pickup-${f}-${index}`]);
                              return copy;
                            });

                            const allFilled = updated.every((p) => p.location.trim() !== "" && p.date !== "" && p.hour !== "");
                            if (allFilled) setPickUpPlaceError("");
                          }}
                          styles={customSelectStyles}
                          classNamePrefix={invalidFields[`pickup-location-${index}`] ? "select-error" : "select"}
                        />
                        {invalidFields[`pickup-location-${index}`] && (
                          <p className="create-load-data-error-text">{t("errors.locationRequired")}</p>
                        )}
                      </div>

                      <div className="create-load-input-row tighter-row">
                        {/* 📅 DATE FIELD */}
                        <div className="create-load-date-field-wrapper">
                          <div className="create-load-custom-input-wrapper">
                            <input
                              type="date"
                              value={place.date || ""}
                              onChange={(e) => {
                                handlePickUpPlaceChange(e, index, "date");
                                setInvalidFields((prev) => ({ ...prev, [`pickup-date-${index}`]: false }));
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value.length === 10) validateDateOnBlur(value, index, "pickup");
                              }}
                              className={`create-load-input ${
                                invalidFields[`pickup-date-${index}`] || dateFieldErrors[`pickup-${index}`]
                                  ? "input-error"
                                  : ""
                              }`}
                              ref={(el) => {
                                if (el) dateRefs.current[index] = el;
                              }}
                              onMouseDown={(e) => {
                                const el = e.currentTarget;
                                if (document.activeElement !== el) el.focus();
                                if (typeof el.showPicker === "function") {
                                  try {
                                    el.showPicker();
                                  } catch {}
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  const el = e.currentTarget;
                                  if (typeof el.showPicker === "function") {
                                    try {
                                      el.showPicker();
                                    } catch {}
                                  }
                                }
                              }}
                            />
                            <FaCalendarAlt
                              className="create-load-input-icon clickable"
                              onClick={(e) => {
                                e.preventDefault();
                                const input = dateRefs.current[index];
                                if (input) {
                                  input.focus();
                                  if (typeof input.showPicker === "function") input.showPicker();
                                }
                              }}
                            />
                          </div>
                        </div>

                        {/* ⏰ FIXED TIME / TIME WINDOW */}
                        <div className="create-load-time-field-wrapper">
                          <button
                            type="button"
                            className={`loadevo-schedule-trigger ${
                              conflictDateHours.has(`${place.date}|${place.hour}`) ||
                              place.hourError ||
                              invalidFields[`pickup-hour-${index}`]
                                ? "input-error"
                                : ""
                            }`}
                            onClick={(event) =>
                              openTimeSchedule("pickup", place, event.currentTarget)
                            }
                            aria-haspopup="dialog"
                            aria-expanded={
                              timeSchedulePopup?.kind === "pickup" &&
                              timeSchedulePopup?.id === place._uiId
                            }
                            title="Set pickup time"
                          >
                            <span>
                              {place.hour
                                ? place.scheduleType === "WINDOW" && place.hourEnd
                                  ? `${place.hour}–${place.hourEnd}`
                                  : place.hour
                                : "Set time"}
                            </span>

                            <FiClock aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      <div className="create-load-point-dimensions-block">
                        <label className="create-load-label create-load-point-dimensions-title">
                          {t("fields.pointDimensions")}
                        </label>

                        <div className="create-load-point-dimensions-row">
                          <div className="create-load-point-dimension-field">
                            <label>{t("fields.pointLengthShort")}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={place.length || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "length")}
                              className={`create-load-input ${
                                invalidFields[`pickup-length-${index}`] ? "input-error" : ""
                              }`}
                              placeholder="0.00"
                            />
                            {place.lengthError && (
                              <p className="create-load-error-text">{place.lengthError}</p>
                            )}
                          </div>

                          <div className="create-load-point-dimension-field">
                            <label>{t("fields.pointWidthShort")}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={place.width || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "width")}
                              className={`create-load-input ${
                                invalidFields[`pickup-width-${index}`] ? "input-error" : ""
                              }`}
                              placeholder="0.00"
                            />
                            {place.widthError && (
                              <p className="create-load-error-text">{place.widthError}</p>
                            )}
                          </div>

                          <div className="create-load-point-dimension-field">
                            <label>{t("fields.pointHeightShort")}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={place.height || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "height")}
                              className={`create-load-input ${
                                invalidFields[`pickup-height-${index}`] ? "input-error" : ""
                              }`}
                              placeholder="0.00"
                            />
                            {place.heightError && (
                              <p className="create-load-error-text">{place.heightError}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* ✅ Loading Access (per pickup point) */}
                      <div className="create-load-field-group" style={{ marginTop: -5 }}>
                        <label className="create-load-label">
                          {t("fields.loadingAccess", { defaultValue: "Loading Access" })}
                        </label>

                        <Select
                          className="create-load-fullwidth-select"
                          options={loadingAccessOptions}
                          value={loadingAccessOptions.find((o) => o.value === place.loadingAccess) || null}
                          onChange={(opt) => {
                            const updated = [...pickUpPlaces];
                            updated[index].loadingAccess = opt?.value || null;
                            setPickUpPlaces(updated);

                            setInvalidFields((prev) => {
                              const copy = { ...prev };
                              delete copy[`pickup-loadingAccess-${index}`];
                              return copy;
                            });
                          }}
                          isClearable
                          placeholder={t("fields.loadingAccessPlaceholder", { defaultValue: "Select loading access..." })}
                          styles={customSelectStyles}
                          classNamePrefix={invalidFields[`pickup-loadingAccess-${index}`] ? "select-error" : "select"}
                        />

                        {invalidFields[`pickup-loadingAccess-${index}`] && (
                          <p className="create-load-data-error-text">
                            {t("errors.loadingAccessRequired", { defaultValue: "❌ Loading access is required." })}
                          </p>
                        )}
                      </div>

                      {/* 🆕 Handling Equipment (per pickup point) */}
                      <div className="create-load-field-group" style={{ marginTop: 6 }}>
                        <label className="create-load-label">
                          {t("fields.handlingEquipment", { defaultValue: "Handling equipment" })}
                        </label>

                        <Select
                          className="create-load-fullwidth-select"
                          options={handlingEquipmentOptions}
                          value={handlingEquipmentOptions.find((o) => o.value === place.handlingEquipment) || null}
                          onChange={(opt) => {
                            const updated = [...pickUpPlaces];
                            const v = opt?.value || null;
                            updated[index].handlingEquipment = v;

                            // ✅ if not OTHER, clear free text
                            if (v !== "OTHER") updated[index].handlingEquipmentOther = "";
                            setPickUpPlaces(updated);

                            setInvalidFields((prev) => {
                              const copy = { ...prev };
                              delete copy[`pickup-handlingEquipment-${index}`];
                              return copy;
                            });
                          }}
                          isClearable
                          placeholder={t("fields.handlingEquipmentPlaceholder", { defaultValue: "Select handling equipment..." })}
                          styles={customSelectStyles}
                          classNamePrefix={invalidFields[`pickup-handlingEquipment-${index}`] ? "select-error" : "select"}
                        />

                        {invalidFields[`pickup-handlingEquipment-${index}`] && (
                          <p className="create-load-data-error-text">
                            {t("errors.handlingEquipmentRequired", { defaultValue: "❌ Handling equipment is required." })}
                          </p>
                        )}
                      </div>

                      {place.handlingEquipment === "OTHER" && (
                        <div className="create-load-field-group" style={{ marginTop: 6 }}>
                          <label className="create-load-label">
                            {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                          </label>

                          <input
                            type="text"
                            value={place.handlingEquipmentOther || ""}
                            placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                            onChange={(e) => {
                              const updated = [...pickUpPlaces];
                              updated[index].handlingEquipmentOther = e.target.value;
                              setPickUpPlaces(updated);

                              setInvalidFields((prev) => {
                                const copy = { ...prev };
                                delete copy[`pickup-handlingEquipmentOther-${index}`];
                                return copy;
                              });
                            }}
                            className={`create-load-input ${
                              invalidFields[`pickup-handlingEquipmentOther-${index}`] ? "input-error" : ""
                            }`}
                          />

                          {invalidFields[`pickup-handlingEquipmentOther-${index}`] && (
                            <p className="create-load-data-error-text">
                              {t("errors.handlingEquipmentOtherRequired", { defaultValue: "❌ Please specify the equipment." })}
                            </p>
                          )}
                        </div>
                      )}

                      {/* 🆕 Reference (per pickup point) */}
                      <div className="create-load-field-group loadevo-v8-reference-field" style={{ marginTop: 6 }}>
                        <label className="create-load-label">
                          {t("fields.reference", { defaultValue: "Reference" })}
                        </label>

                        <input
                          type="text"
                          value={place.reference || ""}
                          placeholder={t("fields.referencePlaceholder", { defaultValue: "e.g. PO 12345, REF cliente..." })}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "reference")}
                          className={`create-load-input ${
                            invalidFields[`pickup-reference-${index}`] ? "input-error" : ""
                          }`}
                        />
                      </div>

                      <div className="create-load-pickup-extra-fields">

                            <div className="loadevo-v8-address-field">
                              <span className="loadevo-v8-field-label">
                                Street address
                              </span>

                              <input
                                type="text"
                                placeholder={t("placeFields.address")}
                                value={place.address || ""}
                                onChange={(e) => handlePickUpPlaceChange(e, index, "address")}
                                className={`create-load-input create-load-main-field ${
                                  invalidFields[`pickup-address-${index}`] ? "input-error" : ""
                                }`}
                              />
                            </div>

                            <div className="create-load-row-tight">
                              <div style={{ flex: 1 }}>
                                <input
                                  type="text"
                                  placeholder={t("placeFields.postalCode")}
                                  value={place.postalCode || ""}
                                  onChange={(e) => handlePickUpPlaceChange(e, index, "postalCode")}
                                  className={`create-load-input create-load-main-field ${
                                    invalidFields[`pickup-postalCode-${index}`] ? "input-error" : ""
                                  }`}
                                />
                              </div>

                              <div style={{ flex: 1 }}>
                                <input
                                  type="text"
                                  placeholder={t("placeFields.city")}
                                  value={place.city || ""}
                                  onChange={(e) => handlePickUpPlaceChange(e, index, "city")}
                                  className={`create-load-input create-load-main-field ${
                                    invalidFields[`pickup-city-${index}`] ? "input-error" : ""
                                  }`}
                                />
                              </div>

                              <div style={{ flex: 1 }}>
                                <input
                                  type="text"
                                  placeholder={t("placeFields.country")}
                                  value={place.country || ""}
                                  onChange={(e) => handlePickUpPlaceChange(e, index, "country")}
                                  className={`create-load-input create-load-main-field ${
                                    invalidFields[`pickup-country-${index}`] ? "input-error" : ""
                                  }`}
                                />
                              </div>
                            </div>

                            <div className="loadevo-v8-phone-field">
                              <span className="loadevo-v8-field-label">
                                Contact phone
                              </span>

                              <input
                                type="text"
                                placeholder={t("placeFields.contactPhone")}
                                value={place.contactPhone || ""}
                                onChange={(e) => handlePickUpPlaceChange(e, index, "contactPhone")}
                                className={`create-load-input create-load-main-field ${
                                  invalidFields[`pickup-contactPhone-${index}`] ? "input-error" : ""
                                }`}
                              />
                            </div>

                            <div className="create-load-ai-explainable-field">
<GoogleMapsLinkField
                                value={place.gMapsLink || ""}
                                placeholder={t("placeFields.gMapsLink")}
                                onChange={(e) =>
                                  handlePickUpPlaceChange(
                                    e,
                                    index,
                                    "gMapsLink"
                                  )
                                }
                                invalid={
                                  invalidFields[`pickup-gMapsLink-${index}`]
                                }
                                mainField
                              />

                              {!place.aiGeocode?.proposedLocation?.coordinates && renderAiFieldExplanation(place, "gMapsLink")}
                            </div>

                            <div className="create-load-ai-explainable-field">
                              <input
                                type="text"
                                placeholder={t("placeFields.coordinatesPlaceholder")}
                                value={place.coordinates || ""}
                                onChange={(e) =>
                                  handlePickUpPlaceChange(
                                    e,
                                    index,
                                    "coordinates"
                                  )
                                }
                                className={`create-load-input create-load-main-field ${
                                  invalidFields[`pickup-coordinates-${index}`]
                                    ? "input-error"
                                    : ""
                                }`}
                              />

                              {!place.aiGeocode?.proposedLocation?.coordinates && renderAiFieldExplanation(place, "coordinates")}
                              <GoogleGeoProposal place={place} onConfirm={p => confirmGoogleGeoProposal("pickup", index, p)} onSelect={p => chooseGoogleGeoCandidate("pickup", index, p)} />
                            </div>
                          </div>

                      {dateFieldErrors[`pickup-${index}`] && (
                        <p className="create-load-data-error-text">{dateFieldErrors[`pickup-${index}`]}</p>
                      )}
                      </div>
                    </CreateLoadStopCard>
                  </CreateLoadSortableStop>
                ))}
                  </SortableContext>
                </DndContext>

                {renderSequenceAlert("pickup")}

                {pickUpPlaceError && <p className="create-load-error-text">{pickUpPlaceError}</p>}

                {pickUpPlaces.length >= MAX_POINTS && (
                  <p className="create-load-warning-text">
                    {t("warnings.maxPickupPoints", { max: MAX_POINTS })}
                  </p>
                )}
              </div>
            </section>

            {/* Column 3: Unloading Points */}
            <section className="create-load-atlas-section create-load-atlas-delivery create-load-load-column">
              <div className="loadevo-v6-stops-header">
                <h3 className="create-load-column-title">
                  <span className="create-load-atlas-step loadevo-v6-step-icon">
                    <FiFlag aria-hidden="true" />
                  </span>
                  <span>Descargas</span>
                </h3>

                <button
                  type="button"
                  className="loadevo-v6-add-stop-btn"
                  onClick={addDeliveryPlace}
                  disabled={deliveryPlaces.length >= MAX_POINTS}
                >
                  Adicionar Descarga
                </button>
              </div>

              <div className="create-load-field-group">
                <DndContext
                  sensors={stopDndSensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  autoScroll={false}
                  onDragStart={(event) => startStopDrag(event, "delivery")}
                  onDragEnd={(event) => dropOnStop(event, "delivery")}
                  onDragCancel={finishStopDrag}
                >
                  <SortableContext
                    items={deliveryPlaces.map((place) => place._uiId)}
                    strategy={verticalListSortingStrategy}
                  >
                {deliveryPlaces.map((place, index) => (
                  <CreateLoadSortableStop
                    key={place._uiId}
                    id={place._uiId}
                    kind="delivery"
                    index={index}
                    disabled={reorderLocked}
                    dragInProgress={draggingStop?.kind === "delivery"}
                    onArrowMove={(event) =>
                      nudgeStopWithKeyboard(
                        event,
                        "delivery",
                        place._uiId,
                        index
                      )
                    }
                  >
                    <CreateLoadStopCard
                      kind="delivery"
                      index={index}
                      stop={place}
                      initiallyExpanded={
                        !aiImportJob &&
                        (index === 0 || index === deliveryPlaces.length - 1)
                      }
                      importRevision={aiJourneyScrollRun}
                      openRequest={
                        stopOpenRequest.kind === "delivery" &&
                        stopOpenRequest.index === index
                          ? stopOpenRequest.seq
                          : 0
                      }
                      invalid={
                        Boolean(dateFieldErrors[`delivery-${index}`]) ||
                        Boolean(place.hourError) ||
                        Object.keys(invalidFields).some(
                          (key) =>
                            key.startsWith("delivery-") &&
                            key.endsWith(`-${index}`) &&
                            Boolean(invalidFields[key])
                        )
                      }
                      canDelete={deliveryPlaces.length > 1}
                      onDelete={() => removeDeliveryPlace(index)}
                      onOperationMinutesChange={(value) =>
                        handleDeliveryPlaceChange(
                          { target: { value } },
                          index,
                          "operationMinutes"
                        )
                      }
                      onExpand={() =>
                        setDeliveryPlaces((previous) => {
                          if (previous[index]?.showMoreFields) {
                            return previous;
                          }

                          return previous.map((item, i) =>
                            i === index
                              ? { ...item, showMoreFields: true }
                              : item
                          );
                        })
                      }
                    >
                      <div className="create-load-input-container loadevo-v8-stop-form">
                        <div className="loadevo-v8-section-heading loadevo-v8-heading--location">
                          <FiMapPin aria-hidden="true" />
                          <span>Location &amp; schedule</span>
                        </div>

                        {place.showMoreFields && (
                          <div className="loadevo-v8-section-heading loadevo-v8-heading--address">
                            <FiMapPin aria-hidden="true" />
                            <span>Address &amp; geolocation</span>
                          </div>
                        )}

                        <div className="loadevo-v8-section-heading loadevo-v8-heading--requirements">
                          <FiBox aria-hidden="true" />
                          <span>Unloading requirements</span>
                        </div>

                        <div className="loadevo-v8-section-heading loadevo-v8-heading--contact">
                          <FiFileText aria-hidden="true" />
                          <span>Contact &amp; reference</span>
                        </div>

                      <div className="create-load-delivery-label-row">
                        <label className="create-load-label">{t("fields.deliveryPlace")}</label>
                        {deliveryPlaces.length > 1 && (
                          <button
                            type="button"
                            className="create-load-pickup-delete-btn"
                            onClick={() => removeDeliveryPlace(index)}
                            title={t("actions.removeDeliveryPoint")}
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      <div className="create-load-select-wrapper-left-icon">
                        <CreatableSelect
                          isClearable
                          isMulti={false}
                          options={placeOptions}
                          placeholder={t("placeSelect.placeholder")}
                          value={
                            place.placeId
                              ? placeOptions.find((p) => p.value === place.placeId) || null
                              : place.location
                              ? { label: place.location, value: place.location }
                              : null
                          }
                          formatOptionLabel={(opt, { context }) =>
                            renderStopLocationOption(opt, context, place)
                          }
                          getOptionValue={(opt) => String(opt.value)}
                          onChange={(selectedOption) => {
                            const updated = [...deliveryPlaces];

                            if (selectedOption?.__isNew__) {
                              updated[index] = renameStopLocationPreservingDetails(
                                updated[index],
                                selectedOption
                              );
                            } else if (selectedOption?.fullData) {
                              const p = selectedOption.fullData;
                              updated[index] = {
                                ...updated[index],
                                placeId: String(p.id),
                                isNewPlace: false, // 🆕 existing place selected
                                location: p.placeName,
                                address: p.address,
                                postalCode: p.postalCode,
                                city: p.city,
                                country: p.country,
                                gMapsLink: p.gMapsLink,
                                coordinates: p.coordinates,
                                contactPhone: p.contactPhone,
                                workingHours: p.workingHours ?? null,
                                loadingAccess: p.loadingAccess || "SIDE", // ✅ default: Lateral
                                handlingEquipment: p.handlingEquipment || "FORKLIFT", // ✅ default: Empilhador
                                handlingEquipmentOther: p.handlingEquipmentOther || "",
                              };
                            } else {
                              updated[index] = renameStopLocationPreservingDetails(
                                updated[index],
                                selectedOption
                              );
                            }

                            setDeliveryPlaces(updated);

                            setInvalidFields((prev) => {
                              const copy = { ...prev };
                              delete copy[`delivery-location-${index}`];
                              const fields = [
                                "location",
                                "address",
                                "postalCode",
                                "city",
                                "country",
                                "gMapsLink",
                                "coordinates",
                                "contactPhone",
                              ];
                              fields.forEach((f) => delete copy[`delivery-${f}-${index}`]);
                              return copy;
                            });

                            const allFilled = updated.every((d) => d.location.trim() !== "" && d.date !== "" && d.hour !== "");
                            if (allFilled) setDeliveryPlaceError("");
                          }}
                          styles={customSelectStyles}
                          classNamePrefix={invalidFields[`delivery-location-${index}`] ? "select-error" : "select"}
                        />
                        {invalidFields[`delivery-location-${index}`] && (
                          <p className="create-load-data-error-text">{t("errors.locationRequired")}</p>
                        )}
                      </div>

                      <div className="create-load-input-row tighter-row">
                        {/* 📅 DATE FIELD */}
                        <div className="create-load-delivery-date-field-wrapper">
                          <div className="create-load-custom-input-wrapper">
                            <input
                              type="date"
                              value={place.date || ""}
                              onChange={(e) => {
                                handleDeliveryPlaceChange(e, index, "date");
                                setInvalidFields((prev) => {
                                  const copy = { ...prev };
                                  delete copy[`delivery-date-${index}`];
                                  return copy;
                                });
                                setDateFieldErrors((prev) => {
                                  const copy = { ...prev };
                                  delete copy[`delivery-${index}`];
                                  return copy;
                                });
                              }}
                              onBlur={(e) => validateDateOnBlur(e.target.value, index, "delivery")}
                              className={`create-load-input ${
                                invalidFields[`delivery-date-${index}`] || dateFieldErrors[`delivery-${index}`]
                                  ? "input-error"
                                  : ""
                              }`}
                              ref={(el) => {
                                deliveryDateRefs.current[index] = el;
                              }}
                              onMouseDown={(e) => {
                                const el = e.currentTarget;
                                if (document.activeElement !== el) el.focus();
                                if (typeof el.showPicker === "function") {
                                  try {
                                    el.showPicker();
                                  } catch {}
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  const el = e.currentTarget;
                                  if (typeof el.showPicker === "function") {
                                    try {
                                      el.showPicker();
                                    } catch {}
                                  }
                                }
                              }}
                            />
                            <FaCalendarAlt
                              className="create-load-input-icon clickable"
                              onClick={(e) => {
                                e.preventDefault();
                                const input = deliveryDateRefs.current[index];
                                if (input) {
                                  input.focus();
                                  if (typeof input.showPicker === "function") input.showPicker();
                                }
                              }}
                            />
                          </div>
                        </div>

                        {/* ⏰ FIXED TIME / TIME WINDOW */}
                        <div className="create-load-delivery-time-field-wrapper">
                          <button
                            type="button"
                            className={`loadevo-schedule-trigger ${
                              place.hourError ||
                              invalidFields[`delivery-hour-${index}`]
                                ? "input-error"
                                : ""
                            }`}
                            onClick={(event) =>
                              openTimeSchedule("delivery", place, event.currentTarget)
                            }
                            aria-haspopup="dialog"
                            aria-expanded={
                              timeSchedulePopup?.kind === "delivery" &&
                              timeSchedulePopup?.id === place._uiId
                            }
                            title="Set delivery time"
                          >
                            <span>
                              {place.hour
                                ? place.scheduleType === "WINDOW" && place.hourEnd
                                  ? `${place.hour}–${place.hourEnd}`
                                  : place.hour
                                : "Set time"}
                            </span>

                            <FiClock aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      <div className="create-load-point-dimensions-block">
                        <label className="create-load-label create-load-point-dimensions-title">
                          {t("fields.pointDimensions")}*
                        </label>

                        <div className="create-load-point-dimensions-row">
                          <div className="create-load-point-dimension-field">
                            <label>{t("fields.pointLengthShort")}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={place.length || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "length")}
                              className={`create-load-input ${
                                invalidFields[`delivery-length-${index}`] ? "input-error" : ""
                              }`}
                              placeholder="0.00"
                            />
                            {place.lengthError && (
                              <p className="create-load-error-text">{place.lengthError}</p>
                            )}
                          </div>

                          <div className="create-load-point-dimension-field">
                            <label>{t("fields.pointWidthShort")}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={place.width || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "width")}
                              className={`create-load-input ${
                                invalidFields[`delivery-width-${index}`] ? "input-error" : ""
                              }`}
                              placeholder="0.00"
                            />
                            {place.widthError && (
                              <p className="create-load-error-text">{place.widthError}</p>
                            )}
                          </div>

                          <div className="create-load-point-dimension-field">
                            <label>{t("fields.pointHeightShort")}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={place.height || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "height")}
                              className={`create-load-input ${
                                invalidFields[`delivery-height-${index}`] ? "input-error" : ""
                              }`}
                              placeholder="0.00"
                            />
                            {place.heightError && (
                              <p className="create-load-error-text">{place.heightError}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* ✅ Loading Access (per delivery point) */}
                      <div className="create-load-field-group" style={{ marginTop: 2 }}>
                        <label className="create-load-label">
                          {t("fields.loadingAccess", { defaultValue: "Loading Access" })}
                        </label>

                        <Select
                          className="create-load-fullwidth-select"
                          options={loadingAccessOptions}
                          value={loadingAccessOptions.find((o) => o.value === place.loadingAccess) || null}
                          onChange={(opt) => {
                            const updated = [...deliveryPlaces];
                            updated[index].loadingAccess = opt?.value || null;
                            setDeliveryPlaces(updated);

                            setInvalidFields((prev) => {
                              const copy = { ...prev };
                              delete copy[`delivery-loadingAccess-${index}`];
                              return copy;
                            });
                          }}
                          isClearable
                          placeholder={t("fields.loadingAccessPlaceholder", { defaultValue: "Select loading access..." })}
                          styles={customSelectStyles}
                          classNamePrefix={invalidFields[`delivery-loadingAccess-${index}`] ? "select-error" : "select"}
                        />

                        {invalidFields[`delivery-loadingAccess-${index}`] && (
                          <p className="create-load-data-error-text">
                            {t("errors.loadingAccessRequired", { defaultValue: "❌ Loading access is required." })}
                          </p>
                        )}
                      </div>

                      {/* 🆕 Handling Equipment (per delivery point) */}
                      <div className="create-load-field-group" style={{ marginTop: 6 }}>
                        <label className="create-load-label">
                          {t("fields.handlingEquipment", { defaultValue: "Handling equipment" })}
                        </label>

                        <Select
                          className="create-load-fullwidth-select"
                          options={handlingEquipmentOptions}
                          value={handlingEquipmentOptions.find((o) => o.value === place.handlingEquipment) || null}
                          onChange={(opt) => {
                            const updated = [...deliveryPlaces];
                            const v = opt?.value || null;
                            updated[index].handlingEquipment = v;

                            if (v !== "OTHER") updated[index].handlingEquipmentOther = "";
                            setDeliveryPlaces(updated);

                            setInvalidFields((prev) => {
                              const copy = { ...prev };
                              delete copy[`delivery-handlingEquipment-${index}`];
                              return copy;
                            });
                          }}
                          isClearable
                          placeholder={t("fields.handlingEquipmentPlaceholder", { defaultValue: "Select handling equipment..." })}
                          styles={customSelectStyles}
                          classNamePrefix={invalidFields[`delivery-handlingEquipment-${index}`] ? "select-error" : "select"}
                        />

                        {invalidFields[`delivery-handlingEquipment-${index}`] && (
                          <p className="create-load-data-error-text">
                            {t("errors.handlingEquipmentRequired", { defaultValue: "❌ Handling equipment is required." })}
                          </p>
                        )}
                      </div>

                      {place.handlingEquipment === "OTHER" && (
                        <div className="create-load-field-group" style={{ marginTop: 6 }}>
                          <label className="create-load-label">
                            {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                          </label>

                          <input
                            type="text"
                            value={place.handlingEquipmentOther || ""}
                            placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                            onChange={(e) => {
                              const updated = [...deliveryPlaces];
                              updated[index].handlingEquipmentOther = e.target.value;
                              setDeliveryPlaces(updated);

                              setInvalidFields((prev) => {
                                const copy = { ...prev };
                                delete copy[`delivery-handlingEquipmentOther-${index}`];
                                return copy;
                              });
                            }}
                            className={`create-load-input ${
                              invalidFields[`delivery-handlingEquipmentOther-${index}`] ? "input-error" : ""
                            }`}
                          />

                          {invalidFields[`delivery-handlingEquipmentOther-${index}`] && (
                            <p className="create-load-data-error-text">
                              {t("errors.handlingEquipmentOtherRequired", { defaultValue: "❌ Please specify the equipment." })}
                            </p>
                          )}
                        </div>
                      )}

                      {/* 🆕 Reference (per delivery point) */}
                      <div className="create-load-field-group loadevo-v8-reference-field" style={{ marginTop: 6 }}>
                        <label className="create-load-label">
                          {t("fields.reference", { defaultValue: "Reference" })}
                        </label>

                        <input
                          type="text"
                          value={place.reference || ""}
                          placeholder={t("fields.referencePlaceholder", { defaultValue: "e.g. POD 999, REF cliente..." })}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "reference")}
                          className={`create-load-input ${
                            invalidFields[`delivery-reference-${index}`] ? "input-error" : ""
                          }`}
                        />
                      </div>

                      <div className="create-load-pickup-extra-fields">
                            <div className="loadevo-v8-address-field">
                              <span className="loadevo-v8-field-label">
                                Street address
                              </span>

                              <input
                                type="text"
                                placeholder={t("placeFields.address")}
                                value={place.address || ""}
                                onChange={(e) => handleDeliveryPlaceChange(e, index, "address")}
                                className={`create-load-input ${invalidFields[`delivery-address-${index}`] ? "input-error" : ""}`}
                              />
                            </div>

                            <div className="create-load-row-tight">
                              <div style={{ flex: 1 }}>
                                <label className="loadevo-v6-field-label">
                                  {t("placeFields.postalCode")}
                                </label>
                                <input
                                  type="text"
                                  placeholder={t("placeFields.postalCode")}
                                  value={place.postalCode || ""}
                                  onChange={(e) => handleDeliveryPlaceChange(e, index, "postalCode")}
                                  className={`create-load-input ${
                                    invalidFields[`delivery-postalCode-${index}`] ? "input-error" : ""
                                  }`}
                                />
                              </div>

                              <div style={{ flex: 1 }}>
                                <label className="loadevo-v6-field-label">
                                  {t("placeFields.city")}
                                </label>
                                <input
                                  type="text"
                                  placeholder={t("placeFields.city")}
                                  value={place.city || ""}
                                  onChange={(e) => handleDeliveryPlaceChange(e, index, "city")}
                                  className={`create-load-input ${invalidFields[`delivery-city-${index}`] ? "input-error" : ""}`}
                                />
                              </div>

                              <div style={{ flex: 1 }}>
                                <label className="loadevo-v6-field-label">
                                  {t("placeFields.country")}
                                </label>
                                <input
                                  type="text"
                                  placeholder={t("placeFields.country")}
                                  value={place.country || ""}
                                  onChange={(e) => handleDeliveryPlaceChange(e, index, "country")}
                                  className={`create-load-input ${
                                    invalidFields[`delivery-country-${index}`] ? "input-error" : ""
                                  }`}
                                />
                              </div>
                            </div>

                            <div className="loadevo-v8-phone-field">
                              <span className="loadevo-v8-field-label">
                                Contact phone
                              </span>

                              <input
                                type="text"
                                placeholder={t("placeFields.contactPhone")}
                                value={place.contactPhone || ""}
                                onChange={(e) => handleDeliveryPlaceChange(e, index, "contactPhone")}
                                className={`create-load-input ${
                                  invalidFields[`delivery-contactPhone-${index}`] ? "input-error" : ""
                                }`}
                              />
                            </div>

                            <div className="create-load-ai-explainable-field">
                              <span className="loadevo-v6-field-label">
                                Google Maps link
                              </span>
                              <GoogleMapsLinkField
                                value={place.gMapsLink || ""}
                                placeholder={t("placeFields.gMapsLink")}
                                onChange={(e) =>
                                  handleDeliveryPlaceChange(
                                    e,
                                    index,
                                    "gMapsLink"
                                  )
                                }
                                invalid={
                                  invalidFields[`delivery-gMapsLink-${index}`]
                                }
                              />

                              {!place.aiGeocode?.proposedLocation?.coordinates && renderAiFieldExplanation(place, "gMapsLink")}
                            </div>

                            <div className="create-load-ai-explainable-field">
                              <label className="loadevo-v6-field-label">
                                Coordinates
                              </label>
                              <input
                                type="text"
                                placeholder={t("placeFields.coordinatesPlaceholder")}
                                value={place.coordinates || ""}
                                onChange={(e) =>
                                  handleDeliveryPlaceChange(
                                    e,
                                    index,
                                    "coordinates"
                                  )
                                }
                                className={`create-load-input ${
                                  invalidFields[`delivery-coordinates-${index}`]
                                    ? "input-error"
                                    : ""
                                }`}
                              />

                              {!place.aiGeocode?.proposedLocation?.coordinates && renderAiFieldExplanation(place, "coordinates")}
                              <GoogleGeoProposal place={place} onConfirm={p => confirmGoogleGeoProposal("delivery", index, p)} onSelect={p => chooseGoogleGeoCandidate("delivery", index, p)} />
                            </div>
                          </div>

                      {dateFieldErrors[`delivery-${index}`] && (
                        <p className="create-load-data-error-text">{dateFieldErrors[`delivery-${index}`]}</p>
                      )}
                      </div>
                    </CreateLoadStopCard>
                  </CreateLoadSortableStop>
                ))}
                  </SortableContext>
                </DndContext>

                {renderSequenceAlert("delivery")}

                {deliveryPlaceError && <p className="create-load-error-text">{deliveryPlaceError}</p>}

                {deliveryPlaces.length >= MAX_POINTS && (
                  <p className="create-load-warning-text">
                    {t("warnings.maxDeliveryPoints", { max: MAX_POINTS })}
                  </p>
                )}

              </div>
            </section>

            {/* Column 2: Merchandise & General Information */}
            <section className="create-load-atlas-section create-load-atlas-cargo create-load-load-column">
              <h3 className="create-load-column-title">
                {t("columns.merchandise")}
              </h3>

              <div className="create-load-merchandise-inline-row">
                {/* Weight */}
                <div className="create-load-field-group create-load-weight-field">
                  <label className="create-load-label">
                    {t("fields.weight", { defaultValue: "Weight (kg)" })}
                  </label>

                  <input
                    type="number"
                    value={weightInput}
                    onChange={handleWeightChange}
                    className={`create-load-input ${weightError ? "input-error" : ""}`}
                    placeholder="0"
                  />

                  {weightError && <p className="create-load-error-text">{weightError}</p>}
                </div>

                {/* Merchandise */}
                <div className="create-load-field-group create-load-merchandise-field">
                  <label className="create-load-label">
                    {t("fields.merchandise", { defaultValue: "Merchandise" })}
                  </label>

                  <input
                    type="text"
                    placeholder={t("fields.merchandisePlaceholder", {
                      defaultValue: "e.g. Pallets, Food, Furniture...",
                    })}
                    value={merchandise}
                    onChange={(e) => {
                      setMerchandise(e.target.value);
                      if (merchandiseError) setMerchandiseError("");
                    }}
                    className={`create-load-input ${merchandiseError ? "input-error" : ""}`}
                  />

                  {merchandiseError && <p className="create-load-error-text">{merchandiseError}</p>}
                </div>
              </div>

              {specialDimError && (
                <p className="create-load-error-text create-load-special-dim-error">
                  {specialDimError}
                </p>
              )}

              <div className="create-load-load-dimensions-summary">
                <label className="create-load-label create-load-load-dimensions-title">
                  {t("fields.loadDimensions", {
                    defaultValue: "Dimensões totais da carga",
                  })}
                </label>

                <div className="create-load-load-dimensions-row">
                  <div className="create-load-load-dimension-field">
                    <label>{t("fields.length", { defaultValue: "Length (m)" })}</label>
                    <input
                      type="text"
                      value={lengthInput}
                      readOnly
                      className="create-load-input create-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="create-load-load-dimension-field">
                    <label>{t("fields.width", { defaultValue: "Width (m)" })}</label>
                    <input
                      type="text"
                      value={widthInput}
                      readOnly
                      className="create-load-input create-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="create-load-load-dimension-field">
                    <label>{t("fields.height", { defaultValue: "Height (m)" })}</label>
                    <input
                      type="text"
                      value={heightInput}
                      readOnly
                      className="create-load-input create-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {!pointDimensionSummary.lengthMatches && (
                  <p className="create-load-dimension-warning-text">
                    {t("errors.pickupDeliveryLengthMismatch", {
                      defaultValue:
                        "⚠️ A soma dos comprimentos das recolhas e entregas ainda não coincide.",
                    })}
                  </p>
                )}

                <div className="create-load-dimension-helper-text">
                  {t("fields.loadDimensionsHelper", {
                    defaultValue:
                      "Comprimento calculado pela soma dos pontos. Largura e altura usam o maior valor registado.",
                  })}
                </div>
              </div>
            </section>

          <section className="create-load-atlas-section create-load-atlas-notes">
            <h3 className="create-load-column-title">
              {t("atlas.notesAndInstructions", { defaultValue: "Notes and instructions" })}
            </h3>
          {/* 🆕 Subcontracted fields (ONLY when external carrier) */}
          {showNotes4subcontracted && (
            <>
              {/* 🆕 Notes for Subcontracted */}
              <div className="create-load-field-group">
                <label className="create-load-label">
                  {t("fields.notes4subcontracted.label")}
                </label>

                <textarea
                  rows={3}
                  placeholder={t("fields.notes4subcontracted.placeholder")}
                  value={notes4subcontracted}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.length <= 2000) {
                      setNotes4subcontracted(v);
                      if (notes4subcontractedError) setNotes4subcontractedError("");
                    } else {
                      setNotes4subcontractedError(t("errors.notesMaxChars", { max: 2000 }));
                    }
                  }}
                  className={`create-load-input ${notes4subcontractedError ? "input-error" : ""}`}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    opacity: 0.75,
                    marginTop: 4,
                  }}
                >
                  <span>{t("fields.notes4subcontracted.helper")}</span>
                  <span>{t("fields.notes4subcontracted.counter", { count: notes4subcontracted.length })}</span>
                </div>

                {notes4subcontractedError && (
                  <p className="create-load-error-text">{notes4subcontractedError}</p>
                )}
              </div>
            </>
          )}


          {/* 📝 Notes for Driver */}
          <div className="create-load-field-group">
            <label className="create-load-label">{t("notes.label")}</label>
            <textarea
              rows={3}
              placeholder={t("notes.placeholder")}
              value={notes4driver}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length <= 2000) {
                  setNotes4driver(v);
                  if (notes4driverError) setNotes4driverError("");
                } else {
                  setNotes4driverError(t("errors.notesMaxChars", { max: 2000 }));
                }
              }}
              className={`create-load-input ${notes4driverError ? "input-error" : ""}`}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                opacity: 0.75,
                marginTop: 4,
              }}
            >
              <span>{t("notes.helper")}</span>
              <span>{notes4driver.length}/2000</span>
            </div>
            {notes4driverError && <p className="create-load-error-text">{notes4driverError}</p>}
          </div>


          </section>

          {globalHourFormatError && (
            <p className="create-load-error-text" style={{ marginTop: "1rem" }}>
              {t("errors.invalidHourFormat")}
            </p>
          )}

          {error && <p className="create-load-error-text">{error}</p>}

          {["QUEUED", "PROCESSING"].includes(
            String(aiImportJob?.status || "").toUpperCase()
          ) && (
            <p role="status" className="create-load-loading-text">
              You can edit the form now. Creating the load will be available
              after location verification finishes.
            </p>
          )}

          {!loading ? (
            <div className="create-load-button-group">
              <button className="create-load-cancel-button" onClick={handleCancel} disabled={loading}>
                {loading ? t("actions.cancelling") : t("actions.cancel")}
              </button>
              <button className="create-load-button" onClick={handleCreateLoad} disabled={loading}>
                {loading ? t("actions.creating") : t("actions.createLoad")}
              </button>
            </div>
          ) : (
            <p className="create-load-loading-text">{t("status.saving")}</p>
          )}
        </main>
      </div>
        </div>

        <aside className="loadevo-v6-atlas-shell">
          <button
            type="button"
            className="loadevo-v6-atlas-toggle"
            onClick={() => {
              setAtlasCollapsed((previous) => !previous);
            }}
            aria-controls="loadevo-atlas-panel"
            aria-expanded={!atlasCollapsed}
            aria-label={
              atlasCollapsed
                ? "Expand Route Preview"
                : "Collapse Route Preview"
            }
            title={
              atlasCollapsed
                ? "Expand Route Preview"
                : "Collapse Route Preview"
            }
          >
            <svg
              className="loadevo-v6-atlas-handle-shape"
              viewBox="0 0 34 84"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                className="loadevo-v6-atlas-handle-fill"
                d="M34 0 C34 10 31 15 24 18 L15 23 Q10 27 10 34 L10 50 Q10 57 15 61 L24 66 C31 69 34 74 34 84 Z"
              />
              <path
                className="loadevo-v6-atlas-handle-stroke"
                d="M34 0 C34 10 31 15 24 18 L15 23 Q10 27 10 34 L10 50 Q10 57 15 61 L24 66 C31 69 34 74 34 84"
              />
            </svg>

            <span className="loadevo-v6-atlas-toggle-icon">
              {atlasCollapsed ? (
                <FiChevronLeft aria-hidden="true" />
              ) : (
                <FiChevronRight aria-hidden="true" />
              )}
            </span>
          </button>

          <div
            id="loadevo-atlas-panel"
            className="loadevo-v6-atlas-panel"
          >
            <CreateLoadAtlasPanel
              pickups={pickUpPlaces}
              deliveries={deliveryPlaces}
              importJob={aiImportJob}
              aiJourney={aiImportJob ? (
                <div ref={aiJourneyRef}>
                  <PremiumAiImportProgressExperience
                    job={aiImportJob}
                    stops={coordinateWorkflowStops}
                    onContinueToDetails={scrollToLoadDetails}
                  />
                </div>
              ) : null}
              onEditStop={(kind, index) => {
                setStopOpenRequest((previous) => ({
                  kind,
                  index,
                  seq: previous.seq + 1,
                }));

                if (kind === "pickup") {
                  setPickUpPlaces((previous) =>
                    previous.map((place, i) =>
                      i === index
                        ? { ...place, showMoreFields: true }
                        : place
                    )
                  );
                } else {
                  setDeliveryPlaces((previous) =>
                    previous.map((place, i) =>
                      i === index
                        ? { ...place, showMoreFields: true }
                        : place
                    )
                  );
                }

                window.requestAnimationFrame(() => {
                  document
                    .getElementById(
                      `create-load-atlas-${kind}-${index}`
                    )
                    ?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                });
              }}
            />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CreateLoad;
